import {
  BarChart3,
  Check,
  ChevronDown,
  ClipboardCheck,
  FileCheck2,
  Eye,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
  Download,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "../components/DataTable";
import { BoltInspectionFields, prepareBoltPayload } from "../components/BoltInspectionForm";
import { Empty, ErrorNotice, Loading } from "../components/Feedback";
import { Modal } from "../components/Modal";
import {
  createSamplingRecord,
  createCompactionSampling,
  compactionSamplingCatalogs,
  deleteBoltReport,
  deleteSamplingRecord,
  listBoltReports,
  listConcreteCasts,
  listCompactionLayers,
  listSamplingRecords,
  listReceivedReports,
  listObservedReports,
  listPendingReports,
  listGroupablePendingReports,
  reportReviewApi,
  samplingCatalogs,
  samplingFormCatalog,
  saveBoltReport,
  type SamplingFilters,
  type ReportReviewFilters,
  type ObservedReportFilters,
  type PendingReportFilters,
  type ConcreteCastFilters,
  type CompactionLayerFilters,
  type GroupablePendingReportType,
  type PendingReportRow,
  type SamplingKind,
  toRows,
  updateSamplingRecord,
} from "../lib/api";
import { getErrorMessage } from "../lib/supabase";
import { downloadXlsx } from "../lib/xlsx";
import { getAssignedMaterials } from "../features/quality-specifications/api";
import { useSession } from "../state/SessionContext";
import type { JsonRecord } from "../types";

const tabs = [
  { id: "sampling", label: "Muestreos y pruebas", icon: ClipboardCheck },
  { id: "review", label: "Revisión de informes", icon: FileCheck2 },
  { id: "statistics", label: "Dashboard", icon: BarChart3 },
];

const samplingKinds: { id: SamplingKind; label: string }[] = [
  { id: "steel", label: "Acero de refuerzo" },
  { id: "compaction", label: "Compactación de suelos" },
  { id: "concrete", label: "Concreto" },
  { id: "bolts", label: "Conexiones atornilladas" },
  { id: "ndt", label: "END" },
  { id: "masonry", label: "Mampostería" },
  { id: "aggregates", label: "Pétreos y suelos" },
  { id: "coating", label: "Recubrimiento anticorrosivo" },
];

const STEEL_DESIGNATIONS = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 16, 18] as const;
const steelAnalysisTypes = [
  { value: "producto", label: "Producto" },
  { value: "colada", label: "Colada" },
];
const AGGREGATE_MATERIAL_TYPES = new Set([
  "agregados",
  "materiales pétreos",
  "materiales térreos",
]);

type Option = { value: string; label: string };

type EarthworksWorkType = "Lineal" | "Puntual" | "Acotado";

const EARTHWORKS_WORK_TYPES: EarthworksWorkType[] = ["Acotado", "Lineal", "Puntual"];
const EARTHWORKS_LOCATION_KEYS = [
  "cad_inicial",
  "cad_final",
  "punto_inicial",
  "punto_final",
  "ubicacion_puntual",
  "franja",
  "longitud",
  "area",
] as const;

type FreshResult = {
  id: number;
  resultado: string;
  lmax: string;
  lmin: string;
  unidad_id: string;
};

type FreshTest = {
  entregable_id: string;
  informe_particular: boolean;
  periodo_informe: number | null;
  resultados: FreshResult[];
};

function localToday() {
  const now = new Date();
  return [now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0")].join("-");
}

function formatDisplayDate(value: unknown): string {
  const text = String(value ?? "");
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return text || "—";
}

const EXPORT_HEADERS = ["FECHA", "FRENTE", "SUBFRENTE", "ESTUDIO", "CANTIDAD", "ESTATUS DEL INFORME"];
function normalizeSamplingStatus(value: unknown): string {
  return String(value || "").trim() || "Sin estatus";
}

function displayReportStatus(status: string): string {
  return status === "N/A" ? "Sin informe particular" : status;
}

function exportRows(filename: string, rows: JsonRecord[]) {
  downloadXlsx(filename, EXPORT_HEADERS, rows.map((row) => [
    String(row.fecha || ""), String(row.frente || ""), String(row.subfrente || ""), String(row.estudio || ""),
    typeof row.cantidad === "number" ? row.cantidad : String(row.cantidad ?? ""), normalizeSamplingStatus(row.estatus_del_informe),
  ]), [13, 24, 24, 42, 12, 24]);
}

function catalogArray(catalog: JsonRecord, candidates: string[]) {
  for (const key of candidates) if (Array.isArray(catalog[key])) return catalog[key] as JsonRecord[];
  return [];
}

// The API may expose a creation-specific scope while retaining the full catalogs
// for filters and historical records. Security remains enforced by the RPCs.
function creationCatalogArray(catalog: JsonRecord, kind: "frentes" | "subfrentes") {
  const scopedCandidates = kind === "frentes"
    ? ["frentes_creacion", "frentes_asignados", "frentes_alcance"]
    : ["subfrentes_creacion", "subfrentes_asignados", "subfrentes_alcance"];
  const scoped = catalogArray(catalog, scopedCandidates);
  const hasScopedCatalog = scopedCandidates.some((key) => Array.isArray(catalog[key]));
  return hasScopedCatalog ? scoped : catalogArray(catalog, kind === "frentes" ? ["frentes", "fronts"] : ["subfrentes", "subfronts"]);
}

function rowValue(row: JsonRecord) {
  return String(row.id || row.value || row.codigo || "");
}

function rowLabel(row: JsonRecord) {
  const specificationCode = String(row.codigo || "").trim();
  const specificationTitle = String(row.titulo || "").trim();
  if (specificationCode || specificationTitle) {
    return [specificationCode, specificationTitle].filter(Boolean).join(" — ");
  }
  return String(
    row.frente
      || row.subfrente
      || row.prueba
      || row.actividad_material
      || row.entregable
      || row.laboratorio
      || row.procedencia_n_comun
      || row.procedencia_n_oficial
      || row.nombre
      || row.titulo
      || row.folio
      || row.estatus
      || row.descripcion
      || row.label
      || row.codigo
      || "",
  );
}

function options(rows: JsonRecord[]): Option[] {
  return rows.map((row) => ({ value: rowValue(row), label: rowLabel(row) })).filter((item) => item.value && item.label);
}

function normalizeStatusOptions(value: unknown): Option[] {
  if (!Array.isArray(value)) return [];
  const unique = new Map<string, Option>();
  value.forEach((item) => {
    const rawValue = typeof item === "string" ? item : item && typeof item === "object" ? String((item as JsonRecord).value ?? "") : "";
    const rawLabel = typeof item === "string" ? item : item && typeof item === "object" ? String((item as JsonRecord).label ?? rawValue) : "";
    const option = { value: rawValue.trim(), label: rawLabel.trim() };
    if (option.value && option.label && !unique.has(option.value)) unique.set(option.value, option);
  });
  return [...unique.values()];
}

// An explicit backend denial is authoritative. Older responses that omit the
// per-row flag retain the pre-existing action behavior for non-scoped roles.
function actionAvailable(permission: unknown, allowWhenMissing: boolean) {
  return permission === true || (permission == null && allowWhenMissing);
}

function MultiSelect({ values, items, onChange, placeholder, disabled = false }: {
  values: string[];
  items: Option[];
  onChange: (values: string[]) => void;
  placeholder: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState({ top: 0, left: 0, width: 320 });
  const root = useRef<HTMLDivElement>(null);
  const selected = items.filter((item) => values.includes(item.value));
  const visible = items.filter((item) => item.label.toLocaleLowerCase("es").includes(search.trim().toLocaleLowerCase("es")));

  useEffect(() => {
    function close(event: MouseEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  function toggleOpen() {
    if (disabled) return;
    if (!open && root.current) {
      const rect = root.current.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, 320), window.innerWidth - 16);
      setPosition({
        top: rect.bottom + 340 > window.innerHeight ? Math.max(8, rect.top - 330) : rect.bottom + 5,
        left: Math.min(Math.max(8, rect.left), window.innerWidth - width - 8),
        width,
      });
    }
    setOpen((current) => !current);
  }

  function toggleValue(value: string) {
    onChange(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  }

  return <div className={`multi-combobox${disabled ? " disabled" : ""}`} ref={root}>
    <button type="button" className="multi-combobox-trigger" disabled={disabled} aria-expanded={open} onClick={toggleOpen}>
      <span className="multi-combobox-value">{!selected.length
        ? <span className="multi-placeholder">{placeholder}</span>
        : <><span className="multi-chip">{selected[0].label}<span role="button" tabIndex={0} onClick={(event) => { event.stopPropagation(); toggleValue(selected[0].value); }} onKeyDown={(event) => { if (event.key === "Enter") toggleValue(selected[0].value); }}><X size={13} /></span></span>{selected.length > 1 && <span className="multi-count">+{selected.length - 1} más</span>}</>}
      </span><ChevronDown size={17} />
    </button>
    {open && <div className="multi-combobox-popover" style={position}>
      <div className="multi-search"><Search size={15} /><input autoFocus placeholder="Filtrar…" value={search} onChange={(event) => setSearch(event.target.value)} /></div>
      <div className="multi-options" role="listbox" aria-multiselectable="true">{visible.length ? visible.map((item) => {
        const selectedItem = values.includes(item.value);
        return <label className={`multi-option${selectedItem ? " selected" : ""}`} key={item.value}><input type="checkbox" checked={selectedItem} onChange={() => toggleValue(item.value)} /><span className="multi-check">{selectedItem && <Check size={12} />}</span><span>{item.label}</span></label>;
      }) : <div className="multi-empty">Sin coincidencias</div>}</div>
    </div>}
  </div>;
}

function Menu({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function close(event: MouseEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  function toggle() {
    if (!open && root.current) {
      const rect = root.current.getBoundingClientRect();
      const width = label === "Agregar registro" ? 260 : 180;
      setPosition({ top: rect.bottom + 5, left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)) });
    }
    setOpen((current) => !current);
  }
  return <div className="row-menu" ref={root}><button type="button" className={label === "Agregar registro" ? "button primary" : "icon-button"} title={label} aria-label={label} onClick={toggle}>{label === "Agregar registro" ? <><Plus size={17} /> {label}<ChevronDown size={15} /></> : <MoreVertical size={18} />}</button>{open && <div className={`row-menu-popover${label === "Agregar registro" ? " add-record-menu" : ""}`} style={position} onClick={() => setOpen(false)}>{children}</div>}</div>;
}

export function SamplingPage() {
  const { context } = useSession();
  const allowLegacyActions = String(context?.rol || "").trim().toLocaleLowerCase("es") !== "supervisor";
  const [tab, setTab] = useState("sampling");
  const [rows, setRows] = useState<JsonRecord[]>([]);
  const [allRows, setAllRows] = useState<JsonRecord[]>([]);
  const [catalog, setCatalog] = useState<JsonRecord>({});
  const [canRegister, setCanRegister] = useState(false);
  const [filters, setFilters] = useState<SamplingFilters>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState<SamplingKind | null>(null);
  const [editing, setEditing] = useState<JsonRecord | null>(null);

  async function load(nextFilters = filters) {
    if (!context) return;
    setLoading(true);
    setError("");
    try {
      const [catalogData, samplingRows, boltData, reviewCatalog] = await Promise.all([
        samplingCatalogs(context),
        listSamplingRecords(context),
        listBoltReports(context),
        reportReviewApi("catalogos", context) as Promise<JsonRecord>,
      ]);
      setCatalog(catalogData);
      const samplingCreatePermission = catalogData.puede_crear ?? catalogData.puede_registrar;
      setCanRegister(typeof samplingCreatePermission === "boolean"
        ? samplingCreatePermission
        : reviewCatalog?.puede_registrar === true);
      const fronts = new Map(catalogArray(catalogData, ["frentes", "fronts"]).map((row) => [rowValue(row), rowLabel(row)]));
      const subfronts = new Map(catalogArray(catalogData, ["subfrentes", "subfronts"]).map((row) => [rowValue(row), rowLabel(row)]));
      const deliverables = new Map(catalogArray(catalogData, ["entregables"]).map((row) => [rowValue(row), rowLabel(row)]));
      const boltRecords = toRows(boltData);
      const boltMuestreos = new Set(boltRecords.map((row) => String(row.muestreo_id || "")).filter(Boolean));
      const samplingDetails = new Map(samplingRows.map((row) => [String(row.id || ""), row.detalles ?? null]));
      const normalized = samplingRows.filter((row) => !boltMuestreos.has(String(row.id))).map((row) => ({
        id: row.id,
        fecha_iso: row.fecha,
        fecha: formatDisplayDate(row.fecha),
        frente: fronts.get(String(row.frente_id || "")) || "—",
        subfrente: subfronts.get(String(row.subfrente_id || "")) || "—",
        estudio: row.entregable || deliverables.get(String(row.entregable_id || "")) || "—",
        detalles: row.detalles ?? null,
        cantidad: row.cantidad,
        estatus_del_informe: normalizeSamplingStatus(row.entrega_informe),
        frente_id: row.frente_id,
        subfrente_id: row.subfrente_id,
        entregable_id: row.entregable_id,
        record_type: "sampling",
        puede_editar: typeof row.puede_editar === "boolean" ? row.puede_editar : undefined,
        puede_eliminar: typeof row.puede_eliminar === "boolean" ? row.puede_eliminar : undefined,
        source: row,
      }));
      const boltRows = boltRecords.map((row) => ({
        id: row.id,
        fecha_iso: String(row.created_at || row.fecha || "").slice(0, 10),
        fecha: formatDisplayDate(String(row.created_at || row.fecha || "").slice(0, 10)),
        frente: row.frente || fronts.get(String(row.frente_id || "")) || "—",
        subfrente: row.subfrente || subfronts.get(String(row.subfrente_id || "")) || "—",
        estudio: String(row.entregable || "Conexiones atornilladas"),
        detalles: samplingDetails.get(String(row.muestreo_id || "")) ?? row.detalles ?? null,
        cantidad: row.cantidad_tornillos_inspeccionados || row.cantidad_total_tornillos || "—",
        estatus_del_informe: normalizeSamplingStatus(row.dictamen),
        frente_id: row.frente_id,
        subfrente_id: row.subfrente_id,
        entregable_id: row.entregable_id,
        record_type: "bolts",
        puede_editar: typeof row.puede_editar === "boolean" ? row.puede_editar : undefined,
        puede_eliminar: typeof row.puede_eliminar === "boolean" ? row.puede_eliminar : undefined,
        source: row,
      }));
      const completeRows: JsonRecord[] = [...normalized, ...boltRows].sort((left, right) => String(right.fecha_iso).localeCompare(String(left.fecha_iso)));
      setAllRows(completeRows);
      setRows(completeRows.filter((row) => (!nextFilters.start || String(row.fecha_iso) >= nextFilters.start!)
        && (!nextFilters.end || String(row.fecha_iso) <= nextFilters.end!)
        && (!nextFilters.frontId || String(row.frente_id || "") === nextFilters.frontId)
        && (!nextFilters.subfrontIds?.length || nextFilters.subfrontIds.includes(String(row.subfrente_id || "")))
        && (!nextFilters.deliverableIds?.length || nextFilters.deliverableIds.includes(String(row.entregable_id || "")))
        && (!nextFilters.status || normalizeSamplingStatus(row.estatus_del_informe) === nextFilters.status)));
    } catch (caught) {
      setError(getErrorMessage(caught));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load({}); }, [context]);

  async function remove(row: JsonRecord) {
    if (!row.id || !actionAvailable(row.puede_eliminar, allowLegacyActions) || !confirm("¿Eliminar este registro? Esta acción también retirará sus datos relacionados conforme a las reglas existentes.")) return;
    try {
      if (row.record_type === "bolts") await deleteBoltReport(String(row.id));
      else {
        if (!context) return;
        await deleteSamplingRecord(context, String(row.id));
      }
      await load();
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  return <>
    <div className="page-heading"><div><span className="eyebrow">ASEGURAMIENTO DE CALIDAD</span><h1>Muestreos y pruebas</h1><p>Gestiona muestreos, pruebas y el seguimiento de sus informes en el contexto activo.</p></div>{tab === "sampling" && <div className="heading-actions">{canRegister && <Menu label="Agregar registro">{samplingKinds.map((kind) => <button type="button" key={kind.id} onClick={() => setCreating(kind.id)}><Plus size={16} />{kind.label}</button>)}</Menu>}<button className="button secondary" onClick={() => void load()}><RefreshCw size={17} /> Actualizar</button></div>}</div>
    <div className="tabs certificate-tabs" role="tablist">{tabs.map(({ id, label, icon: Icon }) => <button role="tab" aria-selected={tab === id} className={tab === id ? "active" : ""} key={id} onClick={() => setTab(id)}><Icon size={16} />{label}</button>)}</div>
    {tab === "sampling" && <SamplingPanel catalog={catalog} filters={filters} setFilters={setFilters} rows={rows} statusRows={allRows} loading={loading} error={error} allowLegacyActions={allowLegacyActions} onApply={(next) => void load(next || filters)} onEdit={setEditing} onDelete={(row) => void remove(row)} />}
    {tab === "review" && <ReportReviewPanel onSamplingRefresh={() => load()} />}
    {tab === "statistics" && <SamplingStatistics catalog={catalog} rows={allRows} loading={loading} />}
    {creating === "concrete" && context && <ConcreteSamplingModal catalog={catalog} onClose={() => setCreating(null)} onSaved={async () => { await load(); }} />}
    {creating === "compaction" && context && <CompactionSamplingModal catalog={catalog} onClose={() => setCreating(null)} onSaved={async () => { await load(); }} />}
    {creating && creating !== "concrete" && creating !== "compaction" && context && <SamplingModal kind={creating} catalog={catalog} onClose={() => setCreating(null)} onSaved={async () => { await load(); }} />}
    {editing && <EditSamplingModal row={editing} catalog={catalog} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
  </>;
}

const samplingChartColors = ["#24745f", "#4b91c7", "#d89b3c", "#8a65ad", "#cf665d", "#65a56f", "#64748b"];
type StatisticsFilters = { start: string; end: string; frontId: string; subfrontId: string };

function SamplingStatistics({ catalog, rows, loading }: { catalog: JsonRecord; rows: JsonRecord[]; loading: boolean }) {
  const [filters, setFilters] = useState<StatisticsFilters>({ start: "", end: "", frontId: "", subfrontId: "" });
  const fronts = useMemo(() => catalogArray(catalog, ["frentes", "fronts"]), [catalog]);
  const subfronts = useMemo(() => catalogArray(catalog, ["subfrentes", "subfronts"]).filter((row) => String(row.frente_id || "") === filters.frontId), [catalog, filters.frontId]);
  const invalidDates = Boolean(filters.start && filters.end && filters.end < filters.start);
  const dateRows = useMemo(() => invalidDates ? [] : rows.filter((row) => (!filters.start || String(row.fecha_iso) >= filters.start) && (!filters.end || String(row.fecha_iso) <= filters.end)), [rows, filters.start, filters.end, invalidDates]);
  const generalRows = useMemo(() => dateRows.filter((row) => (!filters.frontId || String(row.frente_id || "") === filters.frontId) && (!filters.subfrontId || String(row.subfrente_id || "") === filters.subfrontId)), [dateRows, filters.frontId, filters.subfrontId]);
  const statusDistribution = useMemo(() => {
    const result = new Map<string, number>();
    generalRows.forEach((row) => { const status = normalizeSamplingStatus(row.estatus_del_informe); result.set(status, (result.get(status) || 0) + 1); });
    return [...result.entries()].map(([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count || a.status.localeCompare(b.status, "es"));
  }, [generalRows]);
  // Integrity invariant: every generalRows record belongs to exactly one status.
  const totalByStatus = useMemo(() => statusDistribution.reduce((sum, row) => sum + row.count, 0), [statusDistribution]);
  const studies = useMemo(() => {
    const counts = new Map<string, number>();
    generalRows.forEach((row) => { const study = String(row.estudio || "").trim(); const label = study && study !== "—" ? study : "Sin estudio"; counts.set(label, (counts.get(label) || 0) + 1); });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([label, total]) => ({ label, total, percentage: generalRows.length ? total / generalRows.length * 100 : 0 }));
  }, [generalRows]);
  const totalDonut = useMemo(() => studies.reduce((sum, row) => sum + row.total, 0), [studies]);
  const frontStudies = useMemo(() => {
    const counts = new Map<string, { front: string; study: string; total: number }>();
    dateRows.forEach((row) => { const front = String(row.frente || "").trim() || "Sin frente"; const study = String(row.estudio || "").trim(); const label = study && study !== "—" ? study : "Sin estudio"; const key = `${front}\u0000${label}`; const current = counts.get(key); counts.set(key, { front, study: label, total: (current?.total || 0) + 1 }); });
    return [...counts.values()];
  }, [dateRows]);
  const categories = useMemo(() => [...new Set(frontStudies.map((row) => row.study))], [frontStudies]);
  const totalBars = useMemo(() => frontStudies.reduce((sum, row) => sum + row.total, 0), [frontStudies]);
  const groupedFronts = useMemo(() => [...new Set(frontStudies.map((row) => row.front))].map((front) => ({ front, values: frontStudies.filter((row) => row.front === front), total: frontStudies.filter((row) => row.front === front).reduce((sum, row) => sum + row.total, 0) })).sort((a, b) => b.total - a.total), [frontStudies]);
  const studySummary = useMemo(() => {
    const collator = new Intl.Collator("es", { sensitivity: "base" });
    const details = new Map<string, { frontId: string; front: string; subfrontId: string; subfront: string; studyId: string; study: string; total: number }>();
    generalRows.forEach((row) => {
      const frontId = String(row.frente_id || ""); const subfrontId = String(row.subfrente_id || ""); const studyId = String(row.entregable_id || "");
      const front = frontId ? String(row.frente || "").trim() || "Sin frente" : "Sin frente";
      const subfront = subfrontId ? String(row.subfrente || "").trim() || "Sin subfrente" : "Sin subfrente";
      const rawStudy = String(row.estudio || "").trim(); const study = rawStudy && rawStudy !== "—" ? rawStudy : "Sin estudio";
      const key = [frontId || `label:${front}`, subfrontId || "sin-subfrente", studyId || `label:${study}`].join("\u0000");
      const current = details.get(key);
      details.set(key, { frontId, front, subfrontId, subfront, studyId, study, total: (current?.total || 0) + 1 });
    });
    const rows = [...details.values()].sort((left, right) => collator.compare(left.front, right.front) || collator.compare(left.subfront, right.subfront) || collator.compare(left.study, right.study));
    const subtotals = new Map<string, number>();
    rows.forEach((row) => { const key = row.frontId || `label:${row.front}`; subtotals.set(key, (subtotals.get(key) || 0) + row.total); });
    return { rows, subtotals, total: rows.reduce((sum, row) => sum + row.total, 0) };
  }, [generalRows]);
  const studyTableRows = useMemo(() => {
    type DetailRow = (typeof studySummary.rows)[number] & { type: "detail"; showFront: boolean; frontRowSpan: number; showSubfront: boolean; subfrontRowSpan: number };
    type SubtotalRow = { type: "subtotal"; frontKey: string; front: string; total: number };
    const result: Array<DetailRow | SubtotalRow> = [];
    for (let frontStart = 0; frontStart < studySummary.rows.length;) {
      const first = studySummary.rows[frontStart]; const frontKey = first.frontId || `label:${first.front}`;
      let frontEnd = frontStart + 1;
      while (frontEnd < studySummary.rows.length && (studySummary.rows[frontEnd].frontId || `label:${studySummary.rows[frontEnd].front}`) === frontKey) frontEnd += 1;
      for (let subfrontStart = frontStart; subfrontStart < frontEnd;) {
        const subfrontKey = first.frontId ? studySummary.rows[subfrontStart].subfrontId || "sin-subfrente" : studySummary.rows[subfrontStart].subfrontId || `label:${studySummary.rows[subfrontStart].subfront}`;
        let subfrontEnd = subfrontStart + 1;
        while (subfrontEnd < frontEnd && (studySummary.rows[subfrontEnd].subfrontId || (first.frontId ? "sin-subfrente" : `label:${studySummary.rows[subfrontEnd].subfront}`)) === subfrontKey) subfrontEnd += 1;
        for (let index = subfrontStart; index < subfrontEnd; index += 1) result.push({ ...studySummary.rows[index], type: "detail", showFront: index === frontStart, frontRowSpan: frontEnd - frontStart, showSubfront: index === subfrontStart, subfrontRowSpan: subfrontEnd - subfrontStart });
        subfrontStart = subfrontEnd;
      }
      result.push({ type: "subtotal", frontKey, front: first.front, total: studySummary.subtotals.get(frontKey) || 0 });
      frontStart = frontEnd;
    }
    return result;
  }, [studySummary]);
  function exportStudySummary() {
    const exportData = studyTableRows.map((row) => row.type === "detail"
      ? [row.front, row.subfront, row.study, row.total]
      : [row.front, "", `SUBTOTAL ${row.front.toLocaleUpperCase("es")}`, row.total]);
    exportData.push(["", "", "TOTAL GENERAL", studySummary.total]);
    downloadXlsx("estudios-por-frente-subfrente.xlsx", ["FRENTE", "SUBFRENTE", "ESTUDIO", "CANTIDAD"], exportData, [24, 28, 52, 12]);
  }
  const maxFrontTotal = Math.max(0, ...groupedFronts.map((row) => row.total));
  let donutPosition = 0;
  const donutStops = studies.map((row, index) => { const start = donutPosition; donutPosition += row.percentage; return `${samplingChartColors[index % samplingChartColors.length]} ${start}% ${donutPosition}%`; });
  if (loading) return <section className="panel statistics-panel"><Loading /></section>;
  return <section className="sampling-statistics">
    <section className="panel statistics-filters"><div><h2>Filtros del Dashboard</h2>{invalidDates && <small className="field-error">La fecha Hasta no puede ser anterior a la fecha Desde.</small>}</div><label>Desde<input type="date" max={filters.end || undefined} value={filters.start} onChange={(event) => setFilters((current) => ({ ...current, start: event.target.value }))} /></label><label>Hasta<input type="date" min={filters.start || undefined} value={filters.end} onChange={(event) => setFilters((current) => ({ ...current, end: event.target.value }))} /></label><label>Frente<select value={filters.frontId} onChange={(event) => setFilters((current) => ({ ...current, frontId: event.target.value, subfrontId: "" }))}><option value="">Todos los frentes</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label><label>Subfrente<select disabled={!filters.frontId} value={filters.subfrontId} onChange={(event) => setFilters((current) => ({ ...current, subfrontId: event.target.value }))}><option value="">{filters.frontId ? "Todos los subfrentes" : "Seleccione un frente"}</option>{subfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label><button className="button ghost" onClick={() => setFilters({ start: "", end: "", frontId: "", subfrontId: "" })}>Limpiar</button></section>
    <section><div className="section-meta"><strong>Estatus de los informes de resultados</strong><span title={`Suma por estatus: ${totalByStatus.toLocaleString("es-MX")}`}>{generalRows.length.toLocaleString("es-MX")} registros</span></div>{statusDistribution.length ? <div className="statistics-grid">{statusDistribution.map(({ status, count }) => <article key={status}><span>{displayReportStatus(status)}</span><strong>{count.toLocaleString("es-MX")}</strong><small>{(generalRows.length > 0 ? count / generalRows.length * 100 : 0).toFixed(1)} % del total</small></article>)}</div> : <Empty />}</section>
    <div className="sampling-chart-grid"><article className="chart-card"><h3>Distribución por tipo de estudio</h3>{studies.length ? <div className="donut-layout"><div className="donut" style={{ background: `conic-gradient(${donutStops.join(",")})` }} aria-label={`Distribución por tipo de estudio. Total: ${totalDonut}`}><span className="donut-total"><small>TOTAL</small><strong>{totalDonut.toLocaleString("es-MX")}</strong></span></div><div className="chart-legend">{studies.map((row, index) => <div key={row.label} title={`${row.label}: ${row.total} (${row.percentage.toFixed(1)} %)`}><i style={{ background: samplingChartColors[index % samplingChartColors.length] }}/><span>{row.label}</span><strong>{row.total} · {row.percentage.toFixed(1)} %</strong></div>)}</div></div> : <div className="chart-empty"><span>Total: {totalDonut}<br/>No hay datos para los filtros seleccionados.</span></div>}</article>
      <article className="chart-card sampling-front-chart"><header className="chart-card-header"><h3>Distribución por frente y tipo de estudio</h3><span>Total: <strong>{totalBars.toLocaleString("es-MX")}</strong></span></header>{groupedFronts.length ? <><div className="sampling-chart-scroll"><div className="stacked-chart">{groupedFronts.map((group) => <div className="stacked-row" key={group.front}><span>{group.front}</span><div>{group.values.map((row) => { const hasRoomForLabel = row.total > 0 && row.total / maxFrontTotal >= 0.06; return <i className="stacked-segment" key={row.study} style={{ width: `${row.total / maxFrontTotal * 100}%`, background: samplingChartColors[categories.indexOf(row.study) % samplingChartColors.length] }} title={`Frente: ${group.front}\nEstudio: ${row.study}\nCantidad: ${row.total}`}>{hasRoomForLabel && <span>{row.total}</span>}</i>; })}</div><strong>{group.total}</strong></div>)}</div></div><div className="compact-legend">{categories.map((category, index) => <span key={category}><i style={{ background: samplingChartColors[index % samplingChartColors.length] }}/>{category}</span>)}</div></> : <div className="chart-empty">No hay registros en el rango de fechas seleccionado.</div>}</article></div>
    <section className="study-summary"><div className="section-meta"><strong>Estudios por frente y subfrente</strong><span>Total general: {studySummary.total.toLocaleString("es-MX")}</span><button type="button" className="button secondary" disabled={!studySummary.rows.length} onClick={exportStudySummary}><Download size={16}/> Descargar Excel</button></div>{studySummary.rows.length ? <div className="table-wrap"><table className="study-summary-table"><thead><tr><th>FRENTE</th><th>SUBFRENTE</th><th>ESTUDIO</th><th className="statistics-quantity-cell">CANTIDAD</th></tr></thead><tbody>{studyTableRows.map((row) => row.type === "detail" ? <tr key={`${row.frontId || row.front}-${row.subfrontId || row.subfront}-${row.studyId || row.study}`}>{row.showFront && <td rowSpan={row.frontRowSpan}>{row.front}</td>}{row.showSubfront && <td rowSpan={row.subfrontRowSpan}>{row.subfront}</td>}<td>{row.study}</td><td className="statistics-quantity-cell">{row.total.toLocaleString("es-MX")}</td></tr> : <tr className="study-subtotal" key={`${row.frontKey}-subtotal`}><td colSpan={3}>SUBTOTAL {row.front.toLocaleUpperCase("es")}</td><td className="statistics-quantity-cell">{row.total.toLocaleString("es-MX")}</td></tr>)}<tr className="study-grand-total"><td colSpan={3}>TOTAL GENERAL</td><td className="statistics-quantity-cell">{studySummary.total.toLocaleString("es-MX")}</td></tr></tbody></table></div> : <div className="chart-empty">No hay datos para los filtros seleccionados.</div>}</section>
  </section>;
}

function SamplingPanel({ catalog, filters, setFilters, rows, statusRows, loading, error, allowLegacyActions, onApply, onEdit, onDelete }: {
  catalog: JsonRecord;
  filters: SamplingFilters;
  setFilters: React.Dispatch<React.SetStateAction<SamplingFilters>>;
  rows: JsonRecord[];
  statusRows: JsonRecord[];
  loading: boolean;
  error: string;
  allowLegacyActions: boolean;
  onApply: (filters?: SamplingFilters) => void;
  onEdit: (row: JsonRecord) => void;
  onDelete: (row: JsonRecord) => void;
}) {
  const fronts = creationCatalogArray(catalog, "frentes");
  const subfronts = catalogArray(catalog, ["subfrentes", "subfronts"]).filter((row) => !filters.frontId || String(row.frente_id || "") === filters.frontId);
  const studies = catalogArray(catalog, ["entregables", "estudios"]);
  const statuses = useMemo(() => [...new Set(statusRows.map((row) => normalizeSamplingStatus(row.estatus_del_informe)))].sort((a, b) => a.localeCompare(b, "es")), [statusRows]);
  function set<K extends keyof SamplingFilters>(key: K, value: SamplingFilters[K]) {
    setFilters((current) => ({ ...current, [key]: value }));
  }
  return <section className="panel">
    <div className="sampling-filter-grid">
      <label>Desde<input type="date" max={filters.end || localToday()} value={filters.start || ""} onChange={(event) => set("start", event.target.value)} /></label>
      <label>Hasta<input type="date" min={filters.start || undefined} max={localToday()} value={filters.end || ""} onChange={(event) => set("end", event.target.value)} /></label>
      <label>Frente<select value={filters.frontId || ""} onChange={(event) => setFilters((current) => ({ ...current, frontId: event.target.value, subfrontIds: [] }))}><option value="">Todos los frentes</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
      <div className="filter-field"><span className="field-label">Subfrentes</span><MultiSelect values={filters.subfrontIds || []} items={options(subfronts)} onChange={(value) => set("subfrontIds", value)} placeholder={filters.frontId ? "Todos los subfrentes" : "Seleccione primero un frente"} disabled={!filters.frontId} /></div>
      <div className="filter-field"><span className="field-label">Estudios</span><MultiSelect values={filters.deliverableIds || []} items={options(studies)} onChange={(value) => set("deliverableIds", value)} placeholder="Todos los estudios" /></div>
      <label>Estatus del informe<select value={filters.status || ""} onChange={(event) => set("status", event.target.value)}><option value="">Todos los estatus</option>{statuses.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
      <div className="sampling-filter-actions"><button className="button primary" onClick={() => onApply(filters)}>Aplicar filtros</button><button className="button ghost" onClick={() => { setFilters({}); onApply({}); }}>Limpiar</button></div>
    </div>
    <div className="section-meta"><strong>Listado de muestreos y pruebas</strong><span>{rows.length} registros</span><button type="button" className="button secondary" disabled={!rows.length} onClick={() => exportRows("muestreos-y-pruebas.xlsx", rows)}><Download size={16} /> Descargar Excel</button></div>
    {error && <ErrorNotice message={error} />}
    {loading ? <Loading /> : <DataTable tableClassName="sampling-table" rows={rows} columns={["fecha", "frente", "subfrente", "estudio", "detalles", "cantidad", "estatus_del_informe"]} columnLabels={{ detalles: "DETALLES" }} actions={(row) => { const canEdit = actionAvailable(row.puede_editar, allowLegacyActions); const canDelete = actionAvailable(row.puede_eliminar, allowLegacyActions); return canEdit || canDelete ? <Menu label="Acciones">{canEdit && <button type="button" onClick={() => onEdit(row)}><Pencil size={16} />Editar</button>}{canDelete && <button type="button" className="danger" onClick={() => onDelete(row)}><Trash2 size={16} />Borrar</button>}</Menu> : null; }} />}
  </section>;
}

const reportColumns = ["fecha_recepcion1", "folio", "estudio", "frente", "subfrente", "estatus", "fecha_muestreo"];
const reportLabels = {
  fecha_recepcion1: "1RA RECEPCIÓN", folio: "FOLIO", estudio: "ESTUDIO", frente: "FRENTE",
  subfrente: "SUBFRENTE", estatus: "ESTATUS", fecha_muestreo: "MUESTREO/PRUEBA",
};

function ReportReviewPanel({ onSamplingRefresh }: { onSamplingRefresh: () => Promise<void> }) {
  const { context } = useSession();
  const [rows, setRows] = useState<JsonRecord[]>([]);
  const [catalog, setCatalog] = useState<JsonRecord>({});
  const [filters, setFilters] = useState<ReportReviewFilters>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [detail, setDetail] = useState<JsonRecord | null>(null);
  const [editingReport, setEditingReport] = useState<JsonRecord | null>(null);
  const [observationsOpen, setObservationsOpen] = useState(false);
  const [addingReport, setAddingReport] = useState(false);
  const fronts = catalogArray(catalog, ["frentes"]);
  const allSubfronts = catalogArray(catalog, ["subfrentes"]);
  const subfronts = allSubfronts.filter((row) => !filters.frontId || String(row.frente_id || "") === filters.frontId);
  const studies = catalogArray(catalog, ["estudios"]).map((row) => ({
    value: String(row.id),
    label: String(row.label || row.entregable),
  }));
  const statuses = normalizeStatusOptions(catalog.estatus);

  async function load(next = filters) {
    if (!context) return;
    setLoading(true); setError("");
    try {
      const [nextRows, nextCatalog] = await Promise.all([
        listReceivedReports(context, next),
        reportReviewApi("catalogos", context) as Promise<JsonRecord>,
      ]);
      setRows(nextRows); setCatalog(nextCatalog || {});
    } catch (caught) { setError(getErrorMessage(caught)); setRows([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load({}); }, [context]);
  useEffect(() => {
    if (!success) return;
    const timer = window.setTimeout(() => setSuccess(""), 3000);
    return () => window.clearTimeout(timer);
  }, [success]);

  async function removeReport(row: JsonRecord) {
    if (!context || !row.puede_eliminar || !confirm("¿Eliminar este informe? La eliminación será lógica y podrá auditarse.")) return;
    try {
      await reportReviewApi("eliminar", context, { id: row.id });
      setSuccess("Informe eliminado correctamente.");
      await load();
    } catch (caught) { setError(getErrorMessage(caught)); }
  }
  function exportReports() {
    downloadXlsx("informes-recibidos.xlsx", ["1RA RECEPCIÓN", "FOLIO", "ESTUDIO", "FRENTE", "SUBFRENTE", "ESTATUS", "MUESTREO/PRUEBA"], rows.map((row) => [
      formatDisplayDate(row.fecha_recepcion1), String(row.folio || ""), String(row.estudio || ""), String(row.frente || ""),
      String(row.subfrente || ""), String(row.estatus || ""), formatDisplayDate(row.fecha_muestreo),
    ]), [16, 20, 40, 22, 22, 18, 18]);
  }
  const renderDate = (row: JsonRecord, key: string) => ["fecha_recepcion1", "fecha_muestreo"].includes(key) ? formatDisplayDate(row[key]) : undefined;
  return <>
    <section className="panel report-review-panel">
      <div className="sampling-filter-grid report-filter-grid">
        <label>Desde<input type="date" max={filters.end} value={filters.start || ""} onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))} /></label>
        <label>Hasta<input type="date" min={filters.start} value={filters.end || ""} onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))} /></label>
        <label className="report-search">Buscar<input type="search" placeholder="Buscar folio, estudio, frente..." value={filters.search || ""} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} /></label>
        <label>Frente<select value={filters.frontId || ""} onChange={(e) => setFilters((f) => ({ ...f, frontId: e.target.value, subfrontIds: [] }))}><option value="">Todos los frentes</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <div className="filter-field"><span className="field-label">Subfrente</span><MultiSelect values={filters.frontId ? filters.subfrontIds || [] : []} items={options(subfronts)} onChange={(subfrontIds) => setFilters((f) => ({ ...f, subfrontIds }))} placeholder={filters.frontId ? "Todos los subfrentes" : "Seleccione un frente"} disabled={!filters.frontId} /></div>
        <div className="filter-field"><span className="field-label">Estudio</span><MultiSelect values={filters.studyIds || []} items={studies} onChange={(studyIds) => setFilters((f) => ({ ...f, studyIds }))} placeholder="Todos los estudios" /></div>
        <label>Estatus<select value={filters.status || ""} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}><option value="">Todos los estatus</option>{statuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}</select></label>
        <div className="sampling-filter-actions"><button className="button primary" onClick={() => void load(filters)}>Aplicar filtros</button><button className="button ghost" onClick={() => { setFilters({}); void load({}); }}>Limpiar</button></div>
      </div>
      <div className="section-meta"><strong>Listado de informes recibidos</strong><span>{rows.length} registros</span>{catalog.puede_registrar === true && <button className="button primary" onClick={() => setAddingReport(true)}><Plus size={16} /> Agregar registro</button>}<button className="button secondary" onClick={() => setObservationsOpen(true)}>Informes con observaciones</button><button className="button secondary" disabled={!rows.length} onClick={exportReports}><Download size={16} /> Descargar Excel</button></div>
      {success && <div className="notice success">{success}</div>}{error && <ErrorNotice message={error} />}
      {loading ? <Loading /> : <DataTable tableClassName="received-reports-table" rows={rows} columns={reportColumns} columnLabels={reportLabels} renderCell={renderDate} actions={(row) => <Menu label="Acciones"><button onClick={() => setDetail(row)}><Eye size={16} />Ver detalles</button>{row.puede_editar === true && <button onClick={() => setEditingReport(row)}><Pencil size={16} />Editar</button>}{row.puede_eliminar === true && <button className="danger" onClick={() => void removeReport(row)}><Trash2 size={16} />Eliminar</button>}</Menu>} />}
    </section>
    {detail && <ReportDetailModal row={detail} onClose={() => setDetail(null)} />}
    {editingReport && <EditReportModal row={editingReport} statuses={statuses} onClose={() => setEditingReport(null)} onSaved={async () => { setEditingReport(null); setSuccess("Informe actualizado correctamente."); await load(); }} />}
    {observationsOpen && <ObservedReportsModal catalog={catalog} onClose={() => setObservationsOpen(false)} />}
    {addingReport && <AddReportModal catalog={catalog} onClose={() => setAddingReport(false)} onRegistered={async () => { setSuccess("Recepción registrada correctamente."); await Promise.all([load(), onSamplingRefresh()]); }} />}
  </>;
}

function ReportDetailModal({ row, onClose }: { row: JsonRecord; onClose: () => void }) {
  return <Modal title="Detalles del informe" onClose={onClose}><div className="report-detail-grid">
    <div><span>Fecha 1ra recepción</span><strong>{formatDisplayDate(row.fecha_recepcion1)}</strong></div>
    <div><span>Fecha revisión</span><strong>{formatDisplayDate(row.fecha_comentarios)}</strong></div>
    <div><span>Fecha recepción final</span><strong>{formatDisplayDate(row.fecha_recepcion_fin)}</strong></div>
    <div className="full"><span>Observaciones</span><p>{String(row.observaciones || "").trim() || "—"}</p></div>
  </div></Modal>;
}

function EditReportModal({ row, statuses, onClose, onSaved }: { row: JsonRecord; statuses: Option[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const { context } = useSession();
  const canEditComplete = row.puede_editar_completo === true;
  const canEditRevision = row.puede_editar_revision === true;
  const [values, setValues] = useState<JsonRecord>({ folio: row.folio || "", fecha_recepcion1: String(row.fecha_recepcion1 || "").slice(0, 10), fecha_comentarios: String(row.fecha_comentarios || "").slice(0, 10), fecha_recepcion_fin: String(row.fecha_recepcion_fin || "").slice(0, 10), observaciones: row.observaciones || "", estatus: row.estatus || "" });
  const [busy, setBusy] = useState(false); const [error, setError] = useState("");
  function set(key: string, value: string) { setValues((current) => ({ ...current, [key]: value })); }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!context || row.puede_editar !== true || (!canEditComplete && !canEditRevision)) return;
    const revisionValues = { fecha_comentarios: values.fecha_comentarios, fecha_recepcion_fin: values.fecha_recepcion_fin, estatus: values.estatus };
    const data = canEditComplete ? { ...revisionValues, folio: values.folio, fecha_recepcion1: values.fecha_recepcion1, observaciones: values.observaciones } : revisionValues;
    setBusy(true); setError("");
    try { await reportReviewApi("editar", context, { id: row.id, datos: data }); await onSaved(); }
    catch (caught) { setError(getErrorMessage(caught)); }
    finally { setBusy(false); }
  }
  return <Modal title="Editar registro" onClose={onClose}><form className="report-edit-form" onSubmit={submit}>
    <div className="report-edit-primary"><label>Folio<input required disabled={!canEditComplete} value={String(values.folio)} onChange={(e) => set("folio", e.target.value)} /></label><label>Estatus<select disabled={!canEditComplete && !canEditRevision} value={String(values.estatus)} onChange={(e) => set("estatus", e.target.value)}><option value="">Seleccione</option>{statuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}</select></label></div><div className="report-edit-dates"><label>Fecha 1ra recepción<input type="date" disabled={!canEditComplete} value={String(values.fecha_recepcion1)} onChange={(e) => set("fecha_recepcion1", e.target.value)} /></label><label>Fecha revisión<input type="date" disabled={!canEditComplete && !canEditRevision} value={String(values.fecha_comentarios)} onChange={(e) => set("fecha_comentarios", e.target.value)} /></label><label>Fecha recepción final<input type="date" disabled={!canEditComplete && !canEditRevision} value={String(values.fecha_recepcion_fin)} onChange={(e) => set("fecha_recepcion_fin", e.target.value)} /></label></div><label className="full">Observaciones<textarea rows={5} disabled={!canEditComplete} value={String(values.observaciones)} onChange={(e) => set("observaciones", e.target.value)} /></label>{error && <div className="full"><ErrorNotice message={error} /></div>}<div className="modal-actions full"><button type="button" className="button ghost" onClick={onClose}>Cancelar</button><button className="button primary" disabled={busy || (!canEditComplete && !canEditRevision)}>{busy ? "Guardando…" : "Guardar"}</button></div>
  </form></Modal>;
}

function AddReportModal({ catalog, onClose, onRegistered }: { catalog: JsonRecord; onClose: () => void; onRegistered: () => Promise<void> }) {
  const { context } = useSession();
  const [rows, setRows] = useState<JsonRecord[]>([]);
  const [filters, setFilters] = useState<PendingReportFilters>({});
  const [selected, setSelected] = useState<JsonRecord[]>([]);
  const [receptionDialogOpen, setReceptionDialogOpen] = useState(false);
  const [folio, setFolio] = useState("");
  const [receptionDate, setReceptionDate] = useState(localToday());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const fronts = catalogArray(catalog, ["frentes"]);
  const studies = catalogArray(catalog, ["estudios"]).map((row) => ({ value: String(row.id || ""), label: String(row.label || row.entregable || "") })).filter((item) => item.value && item.label);

  const groupedType = (row: JsonRecord): GroupablePendingReportType | null => {
    const value = String(row.tipo || "").trim().toLocaleLowerCase("es");
    if (["ndt", "end", "pnd"].includes(value)) return "ndt";
    if (["connections", "connection", "conexiones", "conexiones_atornilladas", "bolts"].includes(value)) return "connections";
    if (["concrete", "concreto"].includes(value)) return "concrete";
    if (["compaction", "compactacion", "compactación"].includes(value)) return "compaction";
    return null;
  };
  const sampleId = (row: JsonRecord) => String(row.muestreo_id || row.id || "");
  const compatible = (row: JsonRecord) => {
    if (!selected.length) return true;
    const first = selected[0];
    if (
      groupedType(first) !== groupedType(row)
      || String(first.entregable_id || "") !== String(row.entregable_id || "")
      || String(first.laboratorio_id || "") !== String(row.laboratorio_id || "")
    ) return false;
    const firstConnectionType = first.tipo_conexion ?? first.connection_type;
    const rowConnectionType = row.tipo_conexion ?? row.connection_type;
    return groupedType(row) !== "connections" || !firstConnectionType || !rowConnectionType || String(firstConnectionType) === String(rowConnectionType);
  };
  function togglePending(row: JsonRecord) {
    if (row.informe_id != null || !compatible(row)) return;
    const id = sampleId(row);
    setSelected((current) => current.some((item) => sampleId(item) === id) ? current.filter((item) => sampleId(item) !== id) : [...current, row]);
    setError(""); setSuccess("");
  }
  function closeReceptionDialog() {
    setReceptionDialogOpen(false);
    setFolio("");
    setReceptionDate(localToday());
    setError("");
  }

  async function load(next = filters) {
    if (!context) return;
    if (next.start && next.end && next.end < next.start) { setError("La fecha Hasta no puede ser anterior a la fecha Desde."); return; }
    setLoading(true); setError("");
    try {
      const [legacy, groupable] = await Promise.all([listPendingReports(context, next), listGroupablePendingReports(context, next)]);
      const groupedIds = new Set(groupable.map(sampleId));
      const combined = [...groupable, ...legacy.filter((row) => !groupedIds.has(sampleId(row)) && !groupedType(row))];
      setRows(combined);
      setSelected((current) => current.filter((selectedRow) => combined.some((row) => sampleId(row) === sampleId(selectedRow))));
    } catch (caught) { setError(getErrorMessage(caught)); setRows([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load({}); }, [context]);
  useEffect(() => {
    if (!success) return;
    const timer = window.setTimeout(() => setSuccess(""), 3000);
    return () => window.clearTimeout(timer);
  }, [success]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!context || catalog.puede_registrar !== true) return;
    if (!selected.length) { setError("Seleccione al menos un muestreo pendiente antes de registrar."); return; }
    if (!folio.trim() || !receptionDate) { setError("Folio y Fecha 1ra recepción son obligatorios."); return; }
    setBusy(true); setError(""); setSuccess("");
    try {
      const type = groupedType(selected[0]);
      if (type) {
        await reportReviewApi("registrar_agrupado", context, { tipo: type, muestreo_ids: selected.map(sampleId), folio: folio.trim(), fecha_rcp: receptionDate });
      } else {
        const row = selected[0] as PendingReportRow;
        await reportReviewApi("registrar", context, { tipo: row.tipo, datos: { informe_id: row.informe_id, folio: folio.trim(), fecha_rcp: receptionDate } });
      }
      setReceptionDialogOpen(false); setSelected([]); setFolio(""); setReceptionDate(localToday()); setError("");
      await Promise.all([load(filters), onRegistered()]);
      setSuccess("Recepción registrada. Puede capturar otro informe.");
    } catch (caught) { setError(getErrorMessage(caught)); }
    finally { setBusy(false); }
  }
  const selectedStudy = selected.length ? String(selected[0].estudio || "—") : "—";
  return <Modal title="Agregar registro" onClose={onClose} className="modal-wide add-report-modal"><div>
    <div className="pending-filter-grid"><label>Desde<input type="date" max={filters.end} value={filters.start || ""} onChange={(e) => { const start = e.target.value; setFilters((f) => ({ ...f, start, end: f.end && start && f.end < start ? "" : f.end })); setError(""); }} /></label><label>Hasta<input type="date" min={filters.start} value={filters.end || ""} onChange={(e) => { setFilters((f) => ({ ...f, end: e.target.value })); setError(""); }} /></label><label>Frente<select value={filters.frontId || ""} onChange={(e) => setFilters((f) => ({ ...f, frontId: e.target.value }))}><option value="">Todos</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label><div className="filter-field"><span className="field-label">Estudio</span><MultiSelect values={filters.studyIds || []} items={studies} onChange={(studyIds) => setFilters((current) => ({ ...current, studyIds }))} placeholder="Todos los estudios" /></div><div className="sampling-filter-actions"><button type="button" className="button primary" onClick={() => void load(filters)}>Aplicar filtros</button><button type="button" className="button ghost" onClick={() => { setFilters({ studyIds: [] }); void load({ studyIds: [] }); }}>Limpiar</button></div></div>
    <div className="pending-report-table">{loading ? <Loading /> : <DataTable tableClassName="pending-reports-table" rows={rows} columns={["fecha_muestreo", "estudio", "frente", "subfrente", "detalles", "laboratorio"]} columnLabels={{ fecha_muestreo: "FECHA", estudio: "ESTUDIO", frente: "FRENTE", subfrente: "SUBFRENTE", detalles: "DETALLES", laboratorio: "LABORATORIO" }} renderCell={(row, key) => key === "fecha_muestreo" ? formatDisplayDate(row[key]) : undefined} actionsHeader="SELECCIONAR" actionsColumnClassName="pending-report-actions" actions={(row) => { const checked = selected.some((item) => sampleId(item) === sampleId(row)); const disabled = row.informe_id != null || (!checked && !compatible(row)); return groupedType(row) ? <input className="pending-report-selector" type="checkbox" aria-label={`Seleccionar ${String(row.estudio || "muestreo")}`} checked={checked} disabled={disabled} title={disabled ? "El muestreo no es compatible con la selección actual." : ""} onChange={() => togglePending(row)} onClick={(event) => event.stopPropagation()} /> : <button type="button" className="button small secondary pending-report-select-button" onClick={(event) => { event.stopPropagation(); setSelected([row]); setReceptionDialogOpen(true); }}>Seleccionar</button>; }} />}</div>
    {selected.length > 0 && groupedType(selected[0]) && <div className="grouped-reception-actions"><strong>{selected.length} muestreo(s) seleccionado(s)</strong><button type="button" className="button primary" onClick={() => { setFolio(""); setReceptionDate(localToday()); setReceptionDialogOpen(true); }}>Registrar recepción</button></div>}
    {success && <div className="notice success pending-success">{success}</div>}
    {error && !receptionDialogOpen && <ErrorNotice message={error} />}
    {receptionDialogOpen && selected.length > 0 && <Modal title="Registrar recepción" onClose={closeReceptionDialog} className="reception-dialog" backdropClassName="nested-modal-backdrop"><form className="reception-form" onSubmit={submit}><div className="reception-form-grid"><label className="reception-study">Estudio<textarea readOnly rows={2} value={selectedStudy} /></label><label className="reception-count">Muestreos seleccionados<input readOnly value={selected.length} /></label><label className="reception-folio">Folio<input autoFocus required value={folio} onChange={(event) => setFolio(event.target.value)} /></label><label className="reception-date">Fecha 1ra recepción<input required type="date" value={receptionDate} onChange={(event) => setReceptionDate(event.target.value)} /></label></div>{error && <ErrorNotice message={error} />}<div className="modal-actions"><button type="button" className="button ghost" onClick={closeReceptionDialog}>Cerrar</button><button className="button primary" disabled={busy || !folio.trim() || !receptionDate}>{busy ? "Registrando…" : "Registrar recepción"}</button></div></form></Modal>}
  </div></Modal>;
}

function ObservedReportsModal({ catalog, onClose }: { catalog: JsonRecord; onClose: () => void }) {
  const { context } = useSession(); const [rows, setRows] = useState<JsonRecord[]>([]); const [filters, setFilters] = useState<ObservedReportFilters>({}); const [loading, setLoading] = useState(true); const [error, setError] = useState("");
  const statuses = normalizeStatusOptions(catalog.estatus); const labs = catalogArray(catalog, ["laboratorios"]); const fronts = catalogArray(catalog, ["frentes"]);
  async function load(next = filters) { if (!context) return; if (next.start && next.end && next.end < next.start) { setError("La fecha Hasta no puede ser anterior a la fecha Desde."); return; } setLoading(true); setError(""); try { setRows(await listObservedReports(context, next)); } catch (caught) { setError(getErrorMessage(caught)); setRows([]); } finally { setLoading(false); } }
  useEffect(() => { void load({}); }, [context]);
  function exportObserved() { downloadXlsx("informes-con-observaciones.xlsx", ["FOLIO", "RECEPCIÓN", "OBSERVACIONES", "ESTATUS"], rows.map((row) => [String(row.folio || ""), formatDisplayDate(row.fecha_recepcion1), String(row.observaciones || ""), String(row.estatus || "")]), [20, 16, 70, 20]); }
  return <Modal title="Listado de informes con observaciones" onClose={onClose} className="modal-wide observed-reports-modal"><div className="observed-filter-grid">
    <label>Desde<input type="date" max={filters.end} value={filters.start || ""} onChange={(e) => { const start = e.target.value; setFilters((f) => ({ ...f, start, end: f.end && start && f.end < start ? "" : f.end })); setError(""); }} /></label><label>Hasta<input type="date" min={filters.start} value={filters.end || ""} onChange={(e) => { setFilters((f) => ({ ...f, end: e.target.value })); setError(""); }} /></label><label>Estatus<select value={filters.status || ""} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}><option value="">Todos</option>{statuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}</select></label><label>Laboratorio<select value={filters.laboratoryId || ""} onChange={(e) => setFilters((f) => ({ ...f, laboratoryId: e.target.value }))}><option value="">Todos</option>{labs.map((r) => <option key={rowValue(r)} value={rowValue(r)}>{rowLabel(r)}</option>)}</select></label><label>Frente<select value={filters.frontId || ""} onChange={(e) => setFilters((f) => ({ ...f, frontId: e.target.value }))}><option value="">Todos</option>{fronts.map((r) => <option key={rowValue(r)} value={rowValue(r)}>{rowLabel(r)}</option>)}</select></label><div className="sampling-filter-actions"><button className="button primary" onClick={() => void load(filters)}>Aplicar filtros</button><button className="button ghost" onClick={() => { setFilters({}); void load({}); }}>Limpiar</button></div>
  </div><div className="section-meta"><span>{rows.length} registros</span><button className="button secondary" disabled={!rows.length} onClick={exportObserved}><Download size={16} /> Descargar Excel</button></div>{error && <ErrorNotice message={error} />}{loading ? <Loading /> : <DataTable tableClassName="observed-reports-table" rows={rows} columns={["folio", "fecha_recepcion1", "observaciones", "estatus"]} columnLabels={{ folio: "FOLIO", fecha_recepcion1: "RECEPCIÓN", observaciones: "OBSERVACIONES", estatus: "ESTATUS" }} renderCell={(row, key) => key === "fecha_recepcion1" ? formatDisplayDate(row[key]) : undefined} />}</Modal>;
}

function ConcreteSamplingModal({ catalog, onClose, onSaved }: {
  catalog: JsonRecord;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { context } = useSession();
  const isAdmin = String(context?.rol || "").trim().toLocaleLowerCase("es") === "admin";
  const initialValues = (): JsonRecord => ({
    fecha_prueba: localToday(), cantidad: 1, cantidad_entregas: 1,
    periodo_informe: 7, garantia: 28, ctrl_fresco_co: true,
    muestras_co: 0, sup_ext: true, registrar_resistencia: true,
  });
  const [mode, setMode] = useState<"existente" | "nuevo">("existente");
  const [values, setValues] = useState<JsonRecord>(initialValues);
  const [cementingMixtures, setCementingMixtures] = useState<JsonRecord[]>([]);
  const [resistanceUnits, setResistanceUnits] = useState<JsonRecord[]>([]);
  const [castSpecifications, setCastSpecifications] = useState<JsonRecord[]>([]);
  const [castStudies, setCastStudies] = useState<JsonRecord[]>([]);
  const [freshUnits, setFreshUnits] = useState<JsonRecord[]>([]);
  const [freshStudies, setFreshStudies] = useState<JsonRecord[]>([]);
  const [freshTests, setFreshTests] = useState<FreshTest[]>([]);
  const [freshStudyId, setFreshStudyId] = useState("");
  const [casts, setCasts] = useState<JsonRecord[]>([]);
  const [castFilters, setCastFilters] = useState<ConcreteCastFilters>({ date: localToday() });
  const [castPage, setCastPage] = useState(1);
  const [selectedCastId, setSelectedCastId] = useState("");
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [freshUnitsLoading, setFreshUnitsLoading] = useState(true);
  const [dependentCatalogLoading, setDependentCatalogLoading] = useState(false);
  const [castsLoading, setCastsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mixtureRequest = useRef(0);
  const specificationRequest = useRef(0);
  const freshResultId = useRef(1);
  const fronts = creationCatalogArray(catalog, "frentes");
  const areas = catalogArray(catalog, ["areas"]);
  const labs = catalogArray(catalog, ["laboratorios"]);
  const subfronts = creationCatalogArray(catalog, "subfrentes");
  const newCastSubfronts = subfronts.filter((row) => !values.frente_id || String(row.frente_id || "") === String(values.frente_id));
  const filterSubfronts = subfronts.filter((row) => !castFilters.frontId || String(row.frente_id || "") === castFilters.frontId);
  const mixtureOptions = cementingMixtures.map((row) => ({
    id: row.contrato_actividad_material_id || row.id,
    nombre: row.label || row.nombre_contractual || row.actividad_material,
  }));
  const unitOptions = resistanceUnits.map((row) => {
    const name = String(row.nombre || "").trim();
    const symbol = String(row.simbolo || row.símbolo || "").trim();
    return { id: row.id, nombre: name && symbol ? `${name} (${symbol})` : name || symbol };
  });
  const freshUnitOptions = freshUnits.map((row) => ({
    id: rowValue(row),
    nombre: String(row.simbolo || row.símbolo || row.label || row.nombre || ""),
  }));
  const availableFreshStudies = freshStudies.filter((row) => !freshTests.some((test) => test.entregable_id === rowValue(row)));
  const selectedCast = casts.find((row) => String(row.id) === selectedCastId);
  const castsPerPage = 5;
  const castPageCount = Math.max(1, Math.ceil(casts.length / castsPerPage));
  const visibleCasts = casts.slice((castPage - 1) * castsPerPage, castPage * castsPerPage);

  function set(key: string, value: unknown) {
    setValues((current) => ({ ...current, [key]: value }));
  }

  function changeMode(nextMode: "existente" | "nuevo") {
    setMode(nextMode);
    setFreshTests([]);
    setFreshStudies([]);
    setFreshStudyId("");
    setValues((current) => ({ ...current, registrar_resistencia: true }));
    if (nextMode === "nuevo") {
      setSelectedCastId("");
      setCastSpecifications([]);
      setCastStudies([]);
      setValues((current) => ({
        ...current,
        contrato_actividad_material_id: "",
        especificacion_id: "",
        entregable_id: "",
      }));
    }
  }

  async function loadCasts(nextFilters = castFilters) {
    if (!context) return;
    setCastsLoading(true);
    setError("");
    try {
      const nextCasts = await listConcreteCasts(context, nextFilters);
      setCasts(nextCasts);
      setSelectedCastId((current) => nextCasts.some((row) => String(row.id) === current) ? current : "");
      setCastPage(1);
    } catch (caught) {
      setError(getErrorMessage(caught));
      setCasts([]);
    } finally {
      setCastsLoading(false);
    }
  }

  useEffect(() => {
    if (!context) return;
    let active = true;
    Promise.all([
      listConcreteCasts(context, { date: localToday() }),
      samplingFormCatalog("mezclas_cementantes_contrato", context),
      samplingFormCatalog("unidades_resistencia", context),
    ])
      .then(([nextCasts, nextMixtures, nextUnits]) => {
        if (!active) return;
        setCasts(nextCasts);
        setCementingMixtures(nextMixtures);
        setResistanceUnits(nextUnits);
      })
      .catch((caught) => { if (active) setError(getErrorMessage(caught)); })
      .finally(() => {
        if (active) {
          setCatalogLoading(false);
          setCastsLoading(false);
        }
      });
    samplingFormCatalog("unidades_fresco", context)
      .then((nextFreshUnits) => { if (active) setFreshUnits(nextFreshUnits); })
      .catch((caught) => {
        if (active) {
          setFreshUnits([]);
          setError(`No fue posible cargar las unidades de estado fresco: ${getErrorMessage(caught)}`);
        }
      })
      .finally(() => { if (active) setFreshUnitsLoading(false); });
    return () => { active = false; };
  }, [context]);

  async function chooseMix(contractMaterialId: string) {
    const request = ++mixtureRequest.current;
    specificationRequest.current += 1;
    setValues((current) => ({
      ...current,
      contrato_actividad_material_id: contractMaterialId,
      especificacion_id: "",
      entregable_id: "",
    }));
    setCastSpecifications([]);
    setCastStudies([]);
    setFreshStudies([]);
    setFreshTests([]);
    setFreshStudyId("");
    if (!context || !contractMaterialId) return;
    setDependentCatalogLoading(true);
    setError("");
    try {
      const rows = await samplingFormCatalog("especificaciones_material_contrato", context, {
        contrato_actividad_material_id: contractMaterialId,
      });
      if (request === mixtureRequest.current) setCastSpecifications(rows);
    } catch (caught) {
      if (request === mixtureRequest.current) setError(getErrorMessage(caught));
    } finally {
      if (request === mixtureRequest.current) setDependentCatalogLoading(false);
    }
  }

  async function chooseSpecification(specificationId: string) {
    const request = ++specificationRequest.current;
    setValues((current) => ({ ...current, especificacion_id: specificationId, entregable_id: "" }));
    setCastStudies([]);
    setFreshStudies([]);
    setFreshTests([]);
    setFreshStudyId("");
    if (!context || !specificationId) return;
    setDependentCatalogLoading(true);
    setError("");
    try {
      const rows = await samplingFormCatalog("entregables_especificacion", context, {
        especificacion_id: specificationId,
      });
      if (request === specificationRequest.current) {
        setCastStudies(rows);
        setFreshStudies(rows.filter(isFreshStudy));
      }
    } catch (caught) {
      if (request === specificationRequest.current) setError(getErrorMessage(caught));
    } finally {
      if (request === specificationRequest.current) setDependentCatalogLoading(false);
    }
  }

  async function chooseCast(row: JsonRecord) {
    setSelectedCastId(String(row.id));
    setValues((current) => ({
      ...current,
      entregable_id: row.entregable_id || "",
      registrar_resistencia: true,
    }));
    setFreshTests([]);
    setFreshStudies([]);
    setFreshStudyId("");
    const specificationId = String(row.especificacion_id || row.especificacion || row.specification_id || "");
    if (!context || !specificationId) return;
    const request = ++specificationRequest.current;
    setDependentCatalogLoading(true);
    setError("");
    try {
      const rows = await samplingFormCatalog("entregables_especificacion", context, { especificacion_id: specificationId });
      if (request === specificationRequest.current) {
        setFreshStudies(rows.filter(isFreshStudy));
      }
    } catch (caught) {
      if (request === specificationRequest.current) setError(getErrorMessage(caught));
    } finally {
      if (request === specificationRequest.current) setDependentCatalogLoading(false);
    }
  }

  function isFreshStudy(row: JsonRecord) {
    const name = rowLabel(row).toLocaleLowerCase("es");
    const active = row.activo ?? row.active ?? true;
    const applicable = row.aplicable ?? row.applicable ?? true;
    const enabled = (value: unknown) => value !== false && value !== 0 && String(value).toLocaleLowerCase("es") !== "false";
    return enabled(active) && enabled(applicable) && !name.includes("resistencia");
  }

  function blankFreshResult(): FreshResult {
    return { id: freshResultId.current++, resultado: "", lmax: "", lmin: "", unidad_id: "" };
  }

  function addFreshStudy() {
    if (!freshStudyId || freshTests.some((test) => test.entregable_id === freshStudyId)) return;
    setFreshTests((current) => [...current, {
      entregable_id: freshStudyId,
      informe_particular: false,
      periodo_informe: null,
      resultados: [blankFreshResult()],
    }]);
    setFreshStudyId("");
  }

  function updateFreshReport(studyId: string, informeParticular: boolean, reportPeriod: number | null = null) {
    setFreshTests((current) => current.map((test) => test.entregable_id !== studyId ? test : {
      ...test,
      informe_particular: informeParticular,
      periodo_informe: informeParticular ? reportPeriod : null,
    }));
  }

  function updateFreshResult(studyId: string, resultId: number, key: keyof Omit<FreshResult, "id">, value: string) {
    setFreshTests((current) => current.map((test) => test.entregable_id !== studyId ? test : {
      ...test,
      resultados: test.resultados.map((result) => result.id === resultId ? { ...result, [key]: value } : result),
    }));
  }

  function freshValidationError() {
    for (const test of freshTests) {
      if (!test.entregable_id || !test.resultados.length) return "Cada Estudio de estado fresco requiere al menos un resultado.";
      if (typeof test.informe_particular !== "boolean") return "Indique si el Estudio se reporta en un informe diferente al de resistencia.";
      if (test.informe_particular && (test.periodo_informe === null || !Number.isInteger(test.periodo_informe) || test.periodo_informe < 0)) {
        return "El Plazo del informe de cada Estudio con informe diferente es obligatorio y debe ser un entero mayor o igual a cero.";
      }
      for (const result of test.resultados) {
        if (result.resultado === "" || result.unidad_id === "") return "Cada resultado de estado fresco requiere Resultado y Unidad.";
        const numericValues = [result.resultado, result.lmin, result.lmax].filter((value) => value !== "").map(Number);
        if (numericValues.some((value) => !Number.isFinite(value) || value < 0)) return "Los resultados y límites de estado fresco deben ser números mayores o iguales a cero.";
        if (result.lmin !== "" && result.lmax !== "" && Number(result.lmin) > Number(result.lmax)) return "El Límite mínimo no puede ser mayor que el Límite máximo.";
      }
    }
    return "";
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!context) return;
    if (mode === "existente" && !selectedCastId) {
      setError("Seleccione un colado existente.");
      return;
    }
    const registrarResistencia = mode === "nuevo" || Boolean(values.registrar_resistencia);
    if (mode === "existente" && registrarResistencia && !selectedCast?.entregable_id) {
      setError("El colado seleccionado no tiene un Estudio asociado.");
      return;
    }
    if (mode === "existente" && !registrarResistencia && freshTests.length === 0) {
      setError("Debe registrar muestras para resistencia o al menos una prueba en estado fresco.");
      return;
    }
    const freshError = freshValidationError();
    if (freshError) { setError(freshError); return; }
    setBusy(true);
    setError("");
    try {
      await createSamplingRecord("concrete", context, {
        modo: mode,
        colado_id: mode === "existente" ? selectedCastId : null,
        registrar_resistencia: registrarResistencia,
        laboratorio_id: values.laboratorio_id,
        cantidad: registrarResistencia ? Number(values.cantidad) : 0,
        periodo_informe: registrarResistencia ? Number(values.periodo_informe) : null,
        comentarios: values.comentarios || null,
        entregable_id: mode === "existente" ? selectedCast?.entregable_id : values.entregable_id,
        pruebas_fresco: freshTests.map((test) => ({
          entregable_id: test.entregable_id,
          informe_particular: test.informe_particular,
          periodo_informe: test.informe_particular ? test.periodo_informe : null,
          resultados: test.resultados.map(({ resultado, lmax, lmin, unidad_id }) => ({
            resultado: Number(resultado),
            lmax: lmax === "" ? null : Number(lmax),
            lmin: lmin === "" ? null : Number(lmin),
            unidad_id,
          })),
        })),
        ...(mode === "nuevo" ? {
          ...(isAdmin ? { area_id: values.area_id } : {}),
          fecha_prueba: values.fecha_prueba,
          contrato_actividad_material_id: values.contrato_actividad_material_id,
          especificacion_id: values.especificacion_id,
          frente_id: values.frente_id,
          subfrente_id: values.subfrente_id || null,
          proveedor: values.proveedor || null,
          volumen: values.volumen || null,
          elemento: values.elemento,
          ubicacion: values.ubicacion || null,
          resistencia_proyecto: Number(values.resistencia_proyecto),
          resistencia_unidad_id: values.resistencia_unidad_id,
          garantia: Number(values.garantia),
          cantidad_entregas: Number(values.cantidad_entregas),
          ctrl_fresco_co: Boolean(values.ctrl_fresco_co),
          muestras_co: Number(values.muestras_co || 0),
          sup_ext: Boolean(values.sup_ext),
          comentarios_colado: values.comentarios_colado || null,
        } : {}),
      });
      await onSaved();
      setValues(initialValues());
      setCastSpecifications([]);
      setCastStudies([]);
      setFreshStudies([]);
      setFreshTests([]);
      setSelectedCastId("");
      setError("");
      await loadCasts();
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  const registrarResistencia = mode === "nuevo" || Boolean(values.registrar_resistencia);
  const sharedInvalid = [values.laboratorio_id, ...(registrarResistencia ? [values.cantidad, values.periodo_informe] : [])]
    .some((value) => String(value ?? "").trim() === "");
  const deliveries = Number(values.cantidad_entregas);
  const newInvalid = mode === "nuevo" && (
    [values.fecha_prueba, values.contrato_actividad_material_id, values.especificacion_id,
      values.entregable_id, values.frente_id, values.elemento, values.resistencia_proyecto,
      values.resistencia_unidad_id, values.garantia]
      .some((value) => String(value ?? "").trim() === "")
    || !Number.isInteger(deliveries) || deliveries < 1
    || (isAdmin && !values.area_id)
  );

  return <Modal title="Muestreo de concreto, lechada y mortero" className="concrete-sampling-modal" onClose={onClose}>
    <form className="concrete-sampling-form" onSubmit={submit}>
      {error && <div className="notice error full">{error}</div>}
      <div className="concrete-mode-tabs full" role="tablist">
        <button type="button" role="tab" aria-selected={mode === "existente"} className={mode === "existente" ? "active" : ""} onClick={() => changeMode("existente")}>
          <span className="concrete-mode-label"><Check size={17} aria-hidden="true" />Elegir colado existente</span>
          <small>{mode === "existente" ? "Seleccionado" : "Seleccionar"}</small>
        </button>
        <button type="button" role="tab" aria-selected={mode === "nuevo"} className={mode === "nuevo" ? "active" : ""} onClick={() => changeMode("nuevo")}>
          <span className="concrete-mode-label"><Check size={17} aria-hidden="true" />Registrar nuevo colado</span>
          <small>{mode === "nuevo" ? "Seleccionado" : "Seleccionar"}</small>
        </button>
      </div>

      {mode === "existente" ? <section className="concrete-cast-picker full">
        <div className="concrete-cast-filters">
          <label>Fecha<input type="date" max={localToday()} value={castFilters.date || ""} onChange={(event) => { setSelectedCastId(""); setValues((current) => ({ ...current, registrar_resistencia: true })); setCastFilters((current) => ({ ...current, date: event.target.value })); }} required /></label>
          <label>Resistencia<input type="number" min="0" placeholder="Todas" value={castFilters.strength || ""} onChange={(event) => setCastFilters((current) => ({ ...current, strength: event.target.value }))} /></label>
          <label>Frente<select value={castFilters.frontId || ""} onChange={(event) => setCastFilters((current) => ({ ...current, frontId: event.target.value, subfrontId: "" }))}><option value="">Todos</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
          <label>Subfrente<select value={castFilters.subfrontId || ""} disabled={!castFilters.frontId} onChange={(event) => setCastFilters((current) => ({ ...current, subfrontId: event.target.value }))}><option value="">Todos</option>{filterSubfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
          <button type="button" className="button secondary" onClick={() => void loadCasts()}>Aplicar filtros</button>
        </div>
        <div className="concrete-cast-table-wrap">
          {castsLoading ? <Loading /> : casts.length ? <>
            <table className="concrete-cast-table">
              <thead><tr><th aria-label="Selección"></th><th>Elemento</th><th>Ubicación</th><th>Muestras asociadas</th></tr></thead>
              <tbody>{visibleCasts.map((row) => <tr key={String(row.id)} className={selectedCastId === String(row.id) ? "selected" : ""} onClick={() => chooseCast(row)}><td><input type="radio" name="colado" checked={selectedCastId === String(row.id)} onChange={() => chooseCast(row)} aria-label={`Seleccionar ${String(row.elemento || "colado")}`} /></td><td>{String(row.elemento || "—")}</td><td>{String(row.ubicacion || "—")}</td><td>{String(row.muestras_asociadas ?? 0)}</td></tr>)}</tbody>
            </table>
            <div className="concrete-cast-pagination"><span>{casts.length} colados · Página {castPage} de {castPageCount}</span><div>
              <button type="button" className="button ghost" disabled={castPage === 1} onClick={() => setCastPage((current) => Math.max(1, current - 1))}>Anterior</button>
              <button type="button" className="button ghost" disabled={castPage === castPageCount} onClick={() => setCastPage((current) => Math.min(castPageCount, current + 1))}>Siguiente</button>
            </div></div>
          </> : <Empty />}
        </div>
        {selectedCast && <div className="selected-cast-summary">Seleccionado: <strong>{String(selectedCast.elemento || "Colado")}</strong> · {String(selectedCast.estudio || "Sin estudio")}</div>}
      </section> : <section className={`new-cast-fields full${isAdmin ? " with-area" : ""}`}>
        {isAdmin && <label className="cast-area">Área<select required value={String(values.area_id || "")} onChange={(event) => set("area_id", event.target.value)}><option value="">Seleccione…</option>{areas.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>}
        <label className="cast-date">Fecha de colado<input type="date" max={localToday()} value={String(values.fecha_prueba || "")} onChange={(event) => set("fecha_prueba", event.target.value)} required /></label>
        <div className="cast-mix"><SelectField label="Tipo de mezcla" value={values.contrato_actividad_material_id} rows={mixtureOptions} onChange={(value) => void chooseMix(value)} required /></div>
        <div className="cast-specification"><SelectField label="Especificación" value={values.especificacion_id} rows={castSpecifications} onChange={(value) => void chooseSpecification(value)} required disabled={!values.contrato_actividad_material_id || dependentCatalogLoading} /></div>
        <label className="cast-front">Frente<select value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))} required><option value="">Seleccione…</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label className="cast-subfront">Subfrente<select value={String(values.subfrente_id || "")} disabled={!values.frente_id} onChange={(event) => set("subfrente_id", event.target.value)}><option value="">Sin subfrente</option>{newCastSubfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label className="cast-element">Elemento<input value={String(values.elemento || "")} onChange={(event) => set("elemento", event.target.value)} required /></label>
        <label className="cast-location">Ubicación<input value={String(values.ubicacion || "")} onChange={(event) => set("ubicacion", event.target.value)} /></label>
        <label className="cast-provider">Proveedor<input value={String(values.proveedor || "")} onChange={(event) => set("proveedor", event.target.value)} /></label>
        <label className="cast-volume">Volumen, m³<input type="number" min="0" step="any" value={String(values.volumen || "")} onChange={(event) => set("volumen", event.target.value)} /></label>
        <label className="cast-deliveries">Cantidad de entregas<input type="number" min="1" step="1" value={String(values.cantidad_entregas || "")} onChange={(event) => set("cantidad_entregas", event.target.value)} required /></label>
        <div className="cast-study"><SelectField label="Estudio" value={values.entregable_id} rows={castStudies} onChange={(value) => set("entregable_id", value)} required disabled={!values.especificacion_id || dependentCatalogLoading} /></div>
        <label className="cast-strength">Resistencia<input type="number" min="0" step="any" value={String(values.resistencia_proyecto || "")} onChange={(event) => set("resistencia_proyecto", event.target.value)} required /></label>
        <div className="cast-unit"><SelectField label="Unidad" value={values.resistencia_unidad_id} rows={unitOptions} onChange={(value) => set("resistencia_unidad_id", value)} required /></div>
        <label className="cast-age">Edad de garantía (días)<input type="number" min="1" step="1" value={String(values.garantia || "")} onChange={(event) => set("garantia", event.target.value)} required /></label>
        <label className="cast-toggle"><input type="checkbox" checked={Boolean(values.ctrl_fresco_co)} onChange={(event) => set("ctrl_fresco_co", event.target.checked)} />El contratista ejecutó las pruebas en estado fresco.</label>
        <label className="cast-contractor-samples">Muestras del contratista<input type="number" min="0" value={String(values.muestras_co || 0)} onChange={(event) => set("muestras_co", event.target.value)} /></label>
        <label className="cast-toggle"><input type="checkbox" checked={Boolean(values.sup_ext)} onChange={(event) => set("sup_ext", event.target.checked)} />Los trabajos están dentro del alcance de la supervisión externa.</label>
        {dependentCatalogLoading && <div className="cast-catalog-loading">Actualizando opciones relacionadas…</div>}
      </section>}

      {mode === "existente" && <div className="concrete-resistance-toggle-row full">
        <span>Registrar muestras para resistencia</span>
        <button
          type="button"
          role="switch"
          aria-checked={registrarResistencia}
          aria-label="Registrar muestras para resistencia"
          className={`quality-switch${registrarResistencia ? " on" : ""}`}
          onClick={() => set("registrar_resistencia", !registrarResistencia)}
        ><span aria-hidden="true" /></button>
      </div>}

      <section className="concrete-sampling-fields full">
        <div><SelectField label="Laboratorio" value={values.laboratorio_id} rows={labs} onChange={(value) => set("laboratorio_id", value)} required /></div>
        {registrarResistencia && <>
          <label>Número de muestras<input type="number" min="1" value={String(values.cantidad || "")} onChange={(event) => set("cantidad", event.target.value)} required /></label>
          <label>Plazo del informe (días)<input type="number" min="0" value={String(values.periodo_informe || "")} onChange={(event) => set("periodo_informe", event.target.value)} required /></label>
        </>}
      </section>

      <section className="fresh-tests full">
        <header><div><h3>Pruebas en estado fresco</h3><small>Estudios aplicables a la Especificación del colado</small></div></header>
        <div className="fresh-study-picker">
          <SelectField label="Estudio" value={freshStudyId} rows={availableFreshStudies} onChange={setFreshStudyId} disabled={!freshStudies.length || dependentCatalogLoading} />
          <button type="button" className="button secondary" disabled={!freshStudyId} onClick={addFreshStudy}><Plus size={16} /> Agregar estudio</button>
        </div>
        {selectedCastId && !String(selectedCast?.especificacion_id || selectedCast?.especificacion || selectedCast?.specification_id || "") && mode === "existente" && <small className="fresh-help">El colado seleccionado no informa una Especificación.</small>}
        {freshTests.map((test) => {
          const study = freshStudies.find((row) => rowValue(row) === test.entregable_id);
          return <article className="fresh-test" key={test.entregable_id}>
            <div className="fresh-test-title"><strong>{study ? rowLabel(study) : "Estudio"}</strong><button type="button" className="icon-button" aria-label="Eliminar estudio" onClick={() => setFreshTests((current) => current.filter((item) => item.entregable_id !== test.entregable_id))}><Trash2 size={16} /></button></div>
            <label className="fresh-report-toggle"><input type="checkbox" checked={test.informe_particular} onChange={(event) => updateFreshReport(test.entregable_id, event.target.checked)} />Se reporta en un informe diferente al de resistencia.</label>
            {test.informe_particular && <label className="fresh-report-period">Plazo del informe (días)<input type="number" min="0" step="1" required value={test.periodo_informe ?? ""} onChange={(event) => updateFreshReport(test.entregable_id, true, event.target.value === "" ? null : Number(event.target.value))} /></label>}
            <div className="fresh-result-head"><span>Resultado</span><span>Límite mínimo</span><span>Límite máximo</span><span>Unidad</span><span></span></div>
            {test.resultados.map((result) => <div className="fresh-result-row" key={result.id}>
              <label><span>Resultado</span><input type="number" min="0" step="any" required value={result.resultado} onChange={(event) => updateFreshResult(test.entregable_id, result.id, "resultado", event.target.value)} /></label>
              <label><span>Límite mínimo</span><input type="number" min="0" step="any" value={result.lmin} onChange={(event) => updateFreshResult(test.entregable_id, result.id, "lmin", event.target.value)} /></label>
              <label><span>Límite máximo</span><input type="number" min="0" step="any" value={result.lmax} onChange={(event) => updateFreshResult(test.entregable_id, result.id, "lmax", event.target.value)} /></label>
              <div><SelectField label="Unidad" value={result.unidad_id} rows={freshUnitOptions} onChange={(value) => updateFreshResult(test.entregable_id, result.id, "unidad_id", value)} required disabled={freshUnitsLoading} /></div>
              <button type="button" className="icon-button" aria-label="Eliminar resultado" disabled={test.resultados.length === 1} onClick={() => setFreshTests((current) => current.map((item) => item.entregable_id !== test.entregable_id ? item : { ...item, resultados: item.resultados.filter((row) => row.id !== result.id) }))}><Trash2 size={16} /></button>
            </div>)}
            <button type="button" className="button ghost fresh-add-result" onClick={() => setFreshTests((current) => current.map((item) => item.entregable_id !== test.entregable_id ? item : { ...item, resultados: [...item.resultados, blankFreshResult()] }))}><Plus size={15} /> Agregar resultado</button>
          </article>;
        })}
      </section>

      <section className="concrete-comments full">
        {mode === "nuevo" && <label>Comentarios del colado<textarea rows={3} value={String(values.comentarios_colado || "")} onChange={(event) => set("comentarios_colado", event.target.value)} /></label>}
        <label>Comentarios del muestreo<textarea rows={3} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} /></label>
      </section>

      <div className="modal-actions full"><button type="button" className="button ghost" onClick={onClose}>Cancelar</button><button className="button primary" disabled={busy || catalogLoading || castsLoading || dependentCatalogLoading || sharedInvalid || newInvalid || (mode === "existente" && !selectedCastId)}>{busy ? "Guardando…" : "Guardar"}</button></div>
    </form>
  </Modal>;
}

function CompactionSamplingModal({ catalog, onClose, onSaved }: {
  catalog: JsonRecord;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { context } = useSession();
  const [mode, setMode] = useState<"existente" | "nuevo">("existente");
  const [values, setValues] = useState<JsonRecord>({
    fecha_verificacion: localToday(), tipo_obra: "", cantidad: 1, periodo_informe: 7,
    sup_ext: true, especificacion: "", tolerancia: "",
  });
  const [compactionCatalog, setCompactionCatalog] = useState<JsonRecord>({});
  const [layers, setLayers] = useState<JsonRecord[]>([]);
  const [layerFilters, setLayerFilters] = useState<CompactionLayerFilters>({ date: localToday() });
  const [layerPage, setLayerPage] = useState(1);
  const [selectedLayerId, setSelectedLayerId] = useState("");
  const [loading, setLoading] = useState(true);
  const [layersLoading, setLayersLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fronts = creationCatalogArray(catalog, "frentes");
  const labs = catalogArray(catalog, ["laboratorios"]);
  const allSubfronts = creationCatalogArray(catalog, "subfrentes");
  const structures = catalogArray(compactionCatalog, ["estructuras"]);
  const newSubfronts = allSubfronts.filter((row) => !values.frente_id || String(row.frente_id || "") === String(values.frente_id));
  const filterSubfronts = allSubfronts.filter((row) => !layerFilters.frontId || String(row.frente_id || "") === layerFilters.frontId);
  const selectedLayer = layers.find((row) => String(row.id) === selectedLayerId);
  const selectedStructure = structures.find((row) => rowValue(row) === String(values.actividad_material_id || ""));
  const selectedStudy = mode === "nuevo" ? selectedStructure : selectedLayer;
  const studyName = String(selectedStudy?.estudio || "");
  const rowsPerPage = 5;
  const pageCount = Math.max(1, Math.ceil(layers.length / rowsPerPage));
  const visibleLayers = layers.slice((layerPage - 1) * rowsPerPage, layerPage * rowsPerPage);

  function set(key: string, value: unknown) { setValues((current) => ({ ...current, [key]: value })); }

  function setWorkType(tipoObra: EarthworksWorkType | "") {
    setValues((current) => {
      const next: JsonRecord = { ...current, tipo_obra: tipoObra };
      for (const key of EARTHWORKS_LOCATION_KEYS) delete next[key];
      return next;
    });
  }

  function changeMode(nextMode: "existente" | "nuevo") {
    if (nextMode === mode) return;
    setMode(nextMode);
    setSelectedLayerId("");
    setValues((current) => {
      const next: JsonRecord = {
        ...current,
        tipo_obra: "",
        actividad_material_id: "",
        entregable_id: "",
        capa: "",
        especificacion: "",
        tolerancia: "",
      };
      for (const key of EARTHWORKS_LOCATION_KEYS) delete next[key];
      return next;
    });
  }

  function chooseStructure(structureId: string) {
    const structure = structures.find((row) => rowValue(row) === structureId);
    setValues((current) => ({
      ...current,
      actividad_material_id: structureId,
      entregable_id: structure?.entregable_id || "",
    }));
  }

  async function loadLayers(nextFilters = layerFilters) {
    if (!context) return;
    setLayersLoading(true);
    setError("");
    try {
      const nextLayers = await listCompactionLayers(context, nextFilters);
      setLayers(nextLayers);
      setSelectedLayerId((current) => nextLayers.some((row) => String(row.id) === current) ? current : "");
      setLayerPage(1);
    } catch (caught) {
      setError(getErrorMessage(caught));
      setLayers([]);
    } finally {
      setLayersLoading(false);
    }
  }

  useEffect(() => {
    if (!context) return;
    let active = true;
    Promise.all([compactionSamplingCatalogs(context), listCompactionLayers(context, { date: localToday() })])
      .then(([nextCatalog, nextLayers]) => {
        if (!active) return;
        setCompactionCatalog(nextCatalog);
        setLayers(nextLayers);
      })
      .catch((caught) => { if (active) setError(getErrorMessage(caught)); })
      .finally(() => { if (active) { setLoading(false); setLayersLoading(false); } });
    return () => { active = false; };
  }, [context]);

  function chooseLayer(row: JsonRecord) {
    setSelectedLayerId(String(row.id));
    setValues((current) => ({ ...current, entregable_id: row.entregable_id || "" }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!context) return;
    if (mode === "existente" && !selectedLayerId) {
      setError("Seleccione una capa existente.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const basePayload: JsonRecord = {
        modo: mode,
        laboratorio_id: values.laboratorio_id,
        entregable_id: values.entregable_id,
        cantidad: Number(values.cantidad),
        periodo_informe: Number(values.periodo_informe),
        comentarios: values.comentarios || null,
      };
      const payload = mode === "existente"
        ? { ...basePayload, conformacion_terraceria_id: selectedLayerId }
        : {
          ...basePayload,
          fecha_verificacion: values.fecha_verificacion,
          tipo_obra: values.tipo_obra,
          actividad_material_id: values.actividad_material_id,
          frente_id: values.frente_id,
          subfrente_id: values.subfrente_id || null,
          sup_ext: Boolean(values.sup_ext),
          capa: values.capa,
          especificacion: Number(values.especificacion),
          tolerancia: Number(values.tolerancia),
          comentarios_capa: values.comentarios_capa || null,
          ...(values.tipo_obra === "Lineal" ? {
            cad_inicial: Number(values.cad_inicial),
            cad_final: Number(values.cad_final),
            franja: values.franja || null,
          } : {}),
          ...(values.tipo_obra === "Acotado" ? {
            punto_inicial: values.punto_inicial,
            punto_final: values.punto_final,
            franja: values.franja || null,
            longitud: values.longitud || null,
          } : {}),
          ...(values.tipo_obra === "Puntual" ? {
            ubicacion_puntual: values.ubicacion_puntual,
            area: Number(values.area),
          } : {}),
        };
      await createCompactionSampling(context, payload);
      await onSaved();
      setValues({ fecha_verificacion: localToday(), tipo_obra: "", cantidad: 1, periodo_informe: 7, sup_ext: true, especificacion: "", tolerancia: "" });
      setSelectedLayerId("");
      setError("");
      await loadLayers();
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  const quantity = Number(values.cantidad);
  const reportPeriod = Number(values.periodo_informe);
  const sharedInvalid = !values.entregable_id || !values.laboratorio_id
    || !Number.isFinite(quantity) || quantity < 1
    || String(values.periodo_informe ?? "").trim() === "" || !Number.isFinite(reportPeriod) || reportPeriod < 0;
  const specification = Number(values.especificacion);
  const tolerance = Number(values.tolerancia);
  const newInvalid = mode === "nuevo" && (
    [values.fecha_verificacion, values.tipo_obra, values.actividad_material_id, values.frente_id, values.capa, values.especificacion, values.tolerancia]
      .some((value) => String(value ?? "").trim() === "")
    || !Number.isInteger(specification) || specification < 0 || specification > 100
    || !Number.isFinite(tolerance) || tolerance < 0 || tolerance > 100
  );
  const locationInvalid = mode === "nuevo" && (
    (values.tipo_obra === "Acotado" && (!values.punto_inicial || !values.punto_final))
    || (values.tipo_obra === "Lineal" && (
      String(values.cad_inicial ?? "") === ""
      || String(values.cad_final ?? "") === ""
      || !Number.isFinite(Number(values.cad_inicial))
      || !Number.isFinite(Number(values.cad_final))
      || Number(values.cad_inicial) > Number(values.cad_final)
    ))
    || (values.tipo_obra === "Puntual" && (
      !values.ubicacion_puntual
      || String(values.area ?? "").trim() === ""
      || !Number.isFinite(Number(values.area))
      || Number(values.area) <= 0
    ))
  );

  return <Modal title="Verificación de grado de compactación" className="compaction-sampling-modal" onClose={onClose}>
    <form className="compaction-sampling-form" onSubmit={submit}>
      {error && <div className="notice error full">{error}</div>}
      <div className="concrete-mode-tabs full" role="tablist">
        <button type="button" role="tab" aria-selected={mode === "existente"} className={mode === "existente" ? "active" : ""} onClick={() => changeMode("existente")}>
          <span className="concrete-mode-label"><Check size={17} aria-hidden="true" />Elegir capa existente</span><small>{mode === "existente" ? "Seleccionado" : "Seleccionar"}</small>
        </button>
        <button type="button" role="tab" aria-selected={mode === "nuevo"} className={mode === "nuevo" ? "active" : ""} onClick={() => changeMode("nuevo")}>
          <span className="concrete-mode-label"><Check size={17} aria-hidden="true" />Registrar nueva capa</span><small>{mode === "nuevo" ? "Seleccionado" : "Seleccionar"}</small>
        </button>
      </div>

      {mode === "existente" ? <section className="compaction-layer-picker full">
        <div className="compaction-layer-filters">
          <label>Fecha<input type="date" max={localToday()} value={layerFilters.date || ""} onChange={(event) => { setSelectedLayerId(""); setLayerFilters((current) => ({ ...current, date: event.target.value })); }} required /></label>
          <label>Frente<select value={layerFilters.frontId || ""} onChange={(event) => setLayerFilters((current) => ({ ...current, frontId: event.target.value, subfrontId: "" }))}><option value="">Todos</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
          <label>Subfrente<select value={layerFilters.subfrontId || ""} disabled={!layerFilters.frontId} onChange={(event) => setLayerFilters((current) => ({ ...current, subfrontId: event.target.value }))}><option value="">Todos</option>{filterSubfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
          <div><SelectField label="Estructura" value={layerFilters.structureId} rows={structures} onChange={(value) => setLayerFilters((current) => ({ ...current, structureId: value }))} /></div>
          <button type="button" className="button secondary" onClick={() => void loadLayers()}>Aplicar filtros</button>
        </div>
        <div className="compaction-layer-table-wrap">
          {layersLoading ? <Loading /> : layers.length ? <>
            <table className="compaction-layer-table">
              <thead><tr><th aria-label="Selección"></th><th>Frente</th><th>Subfrente</th><th>Capa</th><th>Ubicación</th><th>Sondeos asociados</th></tr></thead>
              <tbody>{visibleLayers.map((row) => <tr key={String(row.id)} className={selectedLayerId === String(row.id) ? "selected" : ""} onClick={() => chooseLayer(row)}>
                <td><input type="radio" name="capa" checked={selectedLayerId === String(row.id)} onChange={() => chooseLayer(row)} aria-label={`Seleccionar capa ${String(row.capa || "")}`} /></td>
                <td>{String(row.frente || "—")}</td><td>{String(row.subfrente || "—")}</td><td>{String(row.capa || "—")}</td><td>{String(row.ubicacion || "—")}</td><td>{String(row.sondeos_asociados ?? 0)}</td>
              </tr>)}</tbody>
            </table>
            <div className="compaction-layer-pagination"><span>{layers.length} capas · Página {layerPage} de {pageCount}</span><div>
              <button type="button" className="button ghost" disabled={layerPage === 1} onClick={() => setLayerPage((current) => Math.max(1, current - 1))}>Anterior</button>
              <button type="button" className="button ghost" disabled={layerPage === pageCount} onClick={() => setLayerPage((current) => Math.min(pageCount, current + 1))}>Siguiente</button>
            </div></div>
          </> : <Empty />}
        </div>
        {selectedLayer && <div className="selected-cast-summary">Seleccionada: <strong>Capa {String(selectedLayer.capa || "—")}</strong> · {String(selectedLayer.estructura || "")}</div>}
      </section> : <section className="new-layer-fields full">
        <label className="layer-work-type">Tipo de obra<select value={String(values.tipo_obra || "")} onChange={(event) => setWorkType(event.target.value as EarthworksWorkType | "")} required><option value="">Seleccione…</option>{EARTHWORKS_WORK_TYPES.map((workType) => <option key={workType} value={workType}>{workType}</option>)}</select></label>
        <div className="layer-structure"><SelectField label="Estructura" value={values.actividad_material_id} rows={structures} onChange={chooseStructure} required /></div>
        <label className="layer-date">Verificación<input type="date" max={localToday()} value={String(values.fecha_verificacion || "")} onChange={(event) => set("fecha_verificacion", event.target.value)} required /></label>
        <label className="layer-front">Frente<select value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))} required><option value="">Seleccione…</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label className="layer-subfront">Subfrente<select value={String(values.subfrente_id || "")} disabled={!values.frente_id} onChange={(event) => set("subfrente_id", event.target.value)}><option value="">Sin subfrente</option>{newSubfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label className="layer-specification">Especificación, %<input type="number" min="0" max="100" step="1" inputMode="numeric" value={String(values.especificacion ?? "")} onChange={(event) => set("especificacion", event.target.value)} required /></label>
        <label className="layer-tolerance">Tolerancia, %<input type="number" min="0" max="100" step="1" inputMode="numeric" value={String(values.tolerancia ?? "")} onChange={(event) => set("tolerancia", event.target.value)} required /></label>
        <label className="layer-toggle"><input type="checkbox" checked={Boolean(values.sup_ext)} onChange={(event) => set("sup_ext", event.target.checked)} />Los trabajos están dentro de los alcances de la Supervisión externa.</label>
        <div className={`layer-location-box ${String(values.tipo_obra || "").toLowerCase()}`}>
          <label className="location-layer">Capa *<input value={String(values.capa || "")} onChange={(event) => set("capa", event.target.value)} required /></label>
          {values.tipo_obra === "Acotado" && <><label className="location-start">Punto inicial<input value={String(values.punto_inicial || "")} onChange={(event) => set("punto_inicial", event.target.value)} required /></label><label className="location-end">Punto final<input value={String(values.punto_final || "")} onChange={(event) => set("punto_final", event.target.value)} required /></label><label className="location-strip">Franja<input value={String(values.franja || "")} onChange={(event) => set("franja", event.target.value)} /></label><label className="location-length">Longitud, m<input type="number" min="0" step="any" inputMode="decimal" value={String(values.longitud || "")} onChange={(event) => set("longitud", event.target.value)} /></label></>}
          {values.tipo_obra === "Lineal" && <><label className="location-start">Cadenamiento inicial<input type="number" step="any" inputMode="decimal" value={String(values.cad_inicial ?? "")} onChange={(event) => set("cad_inicial", event.target.value)} required /></label><label className="location-end">Cadenamiento final<input type="number" step="any" inputMode="decimal" value={String(values.cad_final ?? "")} onChange={(event) => set("cad_final", event.target.value)} required /></label><label className="location-strip">Franja<input value={String(values.franja || "")} onChange={(event) => set("franja", event.target.value)} /></label></>}
          {values.tipo_obra === "Puntual" && <><label className="wide-location">Ubicación<input value={String(values.ubicacion_puntual || "")} onChange={(event) => set("ubicacion_puntual", event.target.value)} required /></label><label className="location-area">Área, m²<input type="number" min="0" step="any" inputMode="decimal" value={String(values.area ?? "")} onChange={(event) => set("area", event.target.value)} required /></label></>}
          <label className="layer-comments">Comentarios de la capa<textarea rows={3} value={String(values.comentarios_capa || "")} onChange={(event) => set("comentarios_capa", event.target.value)} /></label>
        </div>
      </section>}

      <section className="compaction-sampling-fields full">
        <label className="compaction-study">Estudio<input value={studyName} placeholder={mode === "nuevo" ? "Seleccione una estructura" : "Seleccione una capa"} readOnly disabled={!selectedStudy} /></label>
        <div className="compaction-lab"><SelectField label="Laboratorio" value={values.laboratorio_id} rows={labs} onChange={(value) => set("laboratorio_id", value)} required /></div>
        <label className="compaction-count">Número de sondeos<input type="number" min="1" step="1" value={String(values.cantidad || "")} onChange={(event) => set("cantidad", event.target.value)} required /></label>
        <label className="compaction-period">Plazo del informe (días)<input type="number" min="0" step="1" value={String(values.periodo_informe || "")} onChange={(event) => set("periodo_informe", event.target.value)} required /></label>
        <label className="sampling-comments">Comentarios del muestreo<textarea rows={3} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} /></label>
      </section>
      <div className="modal-actions full"><button type="button" className="button ghost" onClick={onClose}>Cancelar</button><button className="button primary" disabled={busy || loading || layersLoading || sharedInvalid || newInvalid || locationInvalid || (mode === "existente" && !selectedLayerId)}>{busy ? "Guardando…" : "Guardar"}</button></div>
    </form>
  </Modal>;
}

function SamplingModal({ kind, catalog, onClose, onSaved }: { kind: SamplingKind; catalog: JsonRecord; onClose: () => void; onSaved: () => Promise<void> }) {
  const { context } = useSession();
  const [values, setValues] = useState<JsonRecord>({
    fecha_prueba: localToday(), cantidad: 1, periodo_informe: 7, entidad: "Supervisión", probetas: 1, piezas: 1,
    rechazo: 0, tornillos_por_especificacion: [], tipo_conexion: "", metodo_pretension: "", dictamen: "", comentarios: "",
    ...(kind === "steel" ? { tipo_analisis: "", colada: "", lote: "" } : {}),
    ...(kind === "masonry" ? { fecha_fab: "", fecha_sum: "", geometria: "Bloque", material: "Arcilla", tipo_pieza: "Maciza", fabricante: "", lote: "", frente_id: "", subfrente_id: "", especificacion_id: "", laboratorio_id: "" } : {}),
  });
  const [aggregateCatalog, setAggregateCatalog] = useState<{
    activities: JsonRecord[];
    deliverables: JsonRecord[];
    origins: JsonRecord[];
    specifications: JsonRecord[];
  }>({ activities: [], deliverables: [], origins: [], specifications: [] });
  const [aggregateLoading, setAggregateLoading] = useState(kind === "aggregates");
  const aggregateMaterialRequest = useRef(0);
  const aggregateSpecificationRequest = useRef(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const label = samplingKinds.find((item) => item.id === kind)?.label || "registro";
  const fronts = creationCatalogArray(catalog, "frentes");
  const subfronts = creationCatalogArray(catalog, "subfrentes").filter((row) => !values.frente_id || String(row.frente_id || "") === String(values.frente_id));
  const contractStudies = catalogArray(catalog, ["entregables", "estudios"]);
  const labs = catalogArray(catalog, ["laboratorios"]);
  const activities = catalogArray(catalog, ["actividades_materiales"]);
  const [ndtSpecifications, setNdtSpecifications] = useState<JsonRecord[]>([]);
  const [ndtStudies, setNdtStudies] = useState<JsonRecord[]>([]);
  const [ndtCatalogLoading, setNdtCatalogLoading] = useState(kind === "ndt");
  const [steelStudies, setSteelStudies] = useState<JsonRecord[]>([]);
  const [steelStudiesLoading, setSteelStudiesLoading] = useState(false);
  const [masonryStudies, setMasonryStudies] = useState<JsonRecord[]>([]);
  const [masonryStudiesLoading, setMasonryStudiesLoading] = useState(false);
  const steelStudiesRequest = useRef(0);
  const [contractSpecifications, setContractSpecifications] = useState<JsonRecord[]>([]);
  const specificationToken = kind === "steel" ? /acero|refuerzo/i : /mampost|block|tabique/i;
  const specificationActivity = activities.find((row) => specificationToken.test(rowLabel(row)));
  const specifications = contractSpecifications;
  function set(key: string, value: unknown) { setValues((current) => ({ ...current, [key]: value })); }

  function resetSpecialForm() {
    setValues({
      fecha_prueba: localToday(),
      cantidad: 1,
      periodo_informe: 7,
      entidad: "Supervisión",
      probetas: 1,
      piezas: 1,
      rechazo: 0,
      ...(kind === "steel" ? { tipo_analisis: "", colada: "", lote: "", proveedor: "", comentarios: "", entregable_id: "" } : {}),
      ...(kind === "masonry" ? { fecha_fab: "", fecha_sum: "", geometria: "Bloque", material: "Arcilla", tipo_pieza: "Maciza", fabricante: "", lote: "", frente_id: "", subfrente_id: "", especificacion_id: "", laboratorio_id: "", comentarios: "" } : {}),
    });
    if (kind === "ndt") setNdtStudies([]);
    if (kind === "steel") setSteelStudies([]);
    if (kind === "masonry") setMasonryStudies([]);
    setError("");
  }

  useEffect(() => {
    if (!context || !["steel", "masonry"].includes(kind)) return;
    let active = true;
    if (!specificationActivity) {
      setContractSpecifications([]);
      setError(`No se encontró la actividad/material aplicable a ${label}.`);
      return () => { active = false; };
    }
    samplingFormCatalog("especificaciones_contrato", context, { actividad_material_id: rowValue(specificationActivity) })
      .then((rows) => { if (active) setContractSpecifications(rows); })
      .catch((caught) => { if (active) setError(getErrorMessage(caught)); });
    return () => { active = false; };
  }, [context, kind, specificationActivity?.id]);

  async function selectSteelSpecification(specificationId: string) {
    const requestId = ++steelStudiesRequest.current;
    setValues((current) => ({ ...current, especificacion_id: specificationId, entregable_id: "" }));
    setSteelStudies([]);
    if (!context || !specificationId) return;
    setSteelStudiesLoading(true);
    setError("");
    try {
      const rows = await samplingFormCatalog("entregables_especificacion", context, { especificacion_id: specificationId });
      if (requestId === steelStudiesRequest.current) {
        setSteelStudies(rows.map((row) => ({ id: row.id, label: row.label || row.entregable })));
      }
    } catch (caught) {
      if (requestId === steelStudiesRequest.current) setError(getErrorMessage(caught));
    } finally {
      if (requestId === steelStudiesRequest.current) setSteelStudiesLoading(false);
    }
  }

  async function selectMasonrySpecification(specificationId: string) {
    setValues((current) => ({ ...current, especificacion_id: specificationId, entregable_id: "" }));
    setMasonryStudies([]);
    if (!context || !specificationId) return;
    setMasonryStudiesLoading(true);
    setError("");
    try {
      setMasonryStudies(await samplingFormCatalog("entregables_especificacion", context, { especificacion_id: specificationId }));
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setMasonryStudiesLoading(false);
    }
  }

  useEffect(() => {
    if (kind !== "ndt" || !context) return;
    let active = true;
    setNdtCatalogLoading(true);
    samplingFormCatalog("especificaciones_soldadura_contrato", context)
      .then((rows) => {
        if (!active) return;
        setNdtSpecifications(rows.map((row) => ({
          id: row.id,
          label: row.label || [row.codigo, row.titulo].filter(Boolean).join(" — "),
        })));
      })
      .catch((caught) => { if (active) setError(getErrorMessage(caught)); })
      .finally(() => { if (active) setNdtCatalogLoading(false); });
    return () => { active = false; };
  }, [context, kind]);

  async function selectNdtSpecification(specificationId: string) {
    setValues((current) => ({ ...current, especificacion_id: specificationId, entregable_id: "" }));
    setNdtStudies([]);
    if (!context || !specificationId) return;
    setNdtCatalogLoading(true);
    setError("");
    try {
      const rows = await samplingFormCatalog("entregables_especificacion", context, { especificacion_id: specificationId });
      setNdtStudies(rows);
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setNdtCatalogLoading(false);
    }
  }

  useEffect(() => {
    if (kind !== "aggregates" || !context) return;
    let active = true;
    setAggregateLoading(true);
    Promise.all([
      getAssignedMaterials(context),
      samplingFormCatalog("procedencias", context),
    ]).then(([assignedMaterials, nextOrigins]) => {
      if (!active) return;
      const aggregateMaterials = assignedMaterials
        .filter((item) => item.is_active !== false && AGGREGATE_MATERIAL_TYPES.has(
          String(item.categoria ?? item.tipo ?? "").trim().toLocaleLowerCase("es"),
        ))
        .map((item) => ({
          ...item,
          actividad_material: item.nombre,
        }))
        .filter((item) => item.actividad_material_id && item.contrato_actividad_material_id);
      setAggregateCatalog((current) => ({ ...current, activities: aggregateMaterials, origins: nextOrigins }));
    }).catch((caught) => {
      if (active) setError(getErrorMessage(caught));
    }).finally(() => {
      if (active) setAggregateLoading(false);
    });
    return () => { active = false; };
  }, [context, kind]);

  async function selectAggregateMaterial(contractMaterialId: string) {
    const requestId = ++aggregateMaterialRequest.current;
    ++aggregateSpecificationRequest.current;
    const selectedMaterial = aggregateCatalog.activities.find((item) => (
      String(item.contrato_actividad_material_id || item.id || "") === contractMaterialId
    ));
    const activityMaterialId = String(selectedMaterial?.actividad_material_id || "");
    setValues((current) => ({
      ...current,
      actividad_material_id: activityMaterialId,
      contrato_actividad_material_id: contractMaterialId,
      entregable_id: "",
      especificacion_id: "",
    }));
    setAggregateCatalog((current) => ({ ...current, deliverables: [], specifications: [] }));
    setError("");
    if (!context || !contractMaterialId || !activityMaterialId) {
      setAggregateLoading(false);
      return;
    }
    setAggregateLoading(true);
    try {
      const relatedSpecifications = await samplingFormCatalog("especificaciones_material_contrato", context, {
        contrato_actividad_material_id: contractMaterialId,
      });
      if (requestId === aggregateMaterialRequest.current) {
        setAggregateCatalog((current) => ({ ...current, deliverables: [], specifications: relatedSpecifications }));
      }
    } catch (caught) {
      if (requestId === aggregateMaterialRequest.current) setError(getErrorMessage(caught));
    } finally {
      if (requestId === aggregateMaterialRequest.current) setAggregateLoading(false);
    }
  }

  async function selectAggregateSpecification(specificationId: string) {
    const requestId = ++aggregateSpecificationRequest.current;
    setValues((current) => ({ ...current, especificacion_id: specificationId, entregable_id: "" }));
    setAggregateCatalog((current) => ({ ...current, deliverables: [] }));
    if (!context || !specificationId) {
      setAggregateLoading(false);
      return;
    }
    setAggregateLoading(true);
    setError("");
    try {
      const rows = await samplingFormCatalog("entregables_especificacion", context, { especificacion_id: specificationId });
      if (requestId === aggregateSpecificationRequest.current) {
        setAggregateCatalog((current) => ({ ...current, deliverables: rows }));
      }
    } catch (caught) {
      if (requestId === aggregateSpecificationRequest.current) setError(getErrorMessage(caught));
    } finally {
      if (requestId === aggregateSpecificationRequest.current) setAggregateLoading(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!context) return;
    if (kind === "masonry") {
      const requiredValues = [values.fecha_prueba, values.geometria, values.material, values.tipo_pieza, values.ancho, values.alto, values.largo, values.resistencia, values.cantidad, values.piezas, values.especificacion_id, values.entregable_id, values.laboratorio_id, values.periodo_informe];
      if (requiredValues.some((value) => String(value ?? "").trim() === "")) {
        setError("Complete todos los campos obligatorios del muestreo de mampostería.");
        return;
      }
      if (values.fecha_fab && values.fecha_prueba && String(values.fecha_fab) > String(values.fecha_prueba)) {
        setError("La fecha de fabricación no puede ser posterior a la fecha de muestreo.");
        return;
      }
      if (values.fecha_fab && values.fecha_sum && String(values.fecha_fab) > String(values.fecha_sum)) {
        setError("La fecha de fabricación no puede ser posterior a la fecha de suministro.");
        return;
      }
    }
    if (kind === "steel" && !String(values.tipo_analisis || "").trim()) {
      setError("Seleccione un Tipo de análisis.");
      return;
    }
    if (kind === "steel" && [values.fecha_prueba, values.designacion, values.especificacion_id, values.entregable_id, values.grado, values.cantidad, values.probetas, values.laboratorio_id, values.periodo_informe, values.frente_id]
      .some((value) => String(value ?? "").trim() === "")) {
      setError("Complete todos los campos obligatorios del muestreo de acero.");
      return;
    }
    if (kind === "ndt") {
      if ([values.fecha_prueba, values.etapa, values.especificacion_id, values.entregable_id, values.frente_id, values.laboratorio_id, values.cantidad, values.periodo_informe]
        .some((value) => String(value ?? "").trim() === "")) {
        setError("Complete todos los campos obligatorios de la inspección de soldadura.");
        return;
      }
      if ((values.etapa === "Taller" && (!String(values.marca || "").trim() || !String(values.consecutivo || "").trim()))
        || (values.etapa === "Campo" && !String(values.ubicacion || "").trim())) {
        setError(values.etapa === "Taller" ? "Marca y Consecutivo son obligatorios en Taller." : "La ubicación de las juntas es obligatoria en Campo.");
        return;
      }
      const total = Number(values.cantidad);
      const rejected = Number(values.rechazo);
      if (!Number.isInteger(total) || total < 1 || !Number.isInteger(rejected) || rejected < 0 || rejected > total) {
        setError("Rechazo debe ser un entero entre 0 y Total juntas.");
        return;
      }
    }
    if (kind === "aggregates") {
      if (!String(values.especificacion_id || "").trim()) {
        setError("Seleccione una Especificación.");
        return;
      }
      if (!String(values.entregable_id || "").trim()) {
        setError("Seleccione un Estudio.");
        return;
      }
      const quantity = Number(values.cantidad);
      if (!Number.isInteger(quantity) || quantity < 1) {
        setError("Muestras debe ser un entero mayor o igual a 1.");
        return;
      }
    }
    setBusy(true);
    setError("");
    try {
      if (kind === "bolts") {
        await saveBoltReport(context, prepareBoltPayload(values));
      } else if (kind === "steel") {
        await createSamplingRecord("steel", context, {
          fecha_prueba: values.fecha_prueba,
          designacion: values.designacion,
          especificacion_id: values.especificacion_id,
          grado: values.grado,
          cantidad: Number(values.cantidad),
          probetas: Number(values.probetas),
          proveedor: values.proveedor || null,
          colada: values.colada || null,
          lote: values.lote || null,
          entregable_id: values.entregable_id,
          tipo_analisis: values.tipo_analisis,
          laboratorio_id: values.laboratorio_id,
          periodo_informe: Number(values.periodo_informe),
          frente_id: values.frente_id,
          subfrente_id: values.subfrente_id || null,
          comentarios: values.comentarios || null,
        });
      } else if (kind === "aggregates") {
        await createSamplingRecord("aggregates", context, {
          fecha_prueba: values.fecha_prueba,
          actividad_material_id: values.actividad_material_id,
          entregable_id: values.entregable_id,
          especificacion_id: values.especificacion_id,
          frente_id: values.frente_id,
          subfrente_id: values.subfrente_id || null,
          lugar_muestreo: values.lugar_muestreo,
          lote: values.lote || null,
          procedencia_id: values.procedencia_id,
          laboratorio_id: values.laboratorio_id,
          entidad: "Supervisión",
          cantidad: Number(values.cantidad),
          periodo_informe: Number(values.periodo_informe),
          comentarios: values.comentarios || null,
        });
      } else if (kind === "masonry") {
        await createSamplingRecord("masonry", context, {
          fecha_prueba: values.fecha_prueba,
          fecha_fab: values.fecha_fab || "",
          fecha_sum: values.fecha_sum || "",
          geometria: values.geometria,
          material: values.material,
          tipo_pieza: values.tipo_pieza,
          ancho: Number(values.ancho),
          alto: Number(values.alto),
          largo: Number(values.largo),
          resistencia: Number(values.resistencia),
          cantidad: Number(values.cantidad),
          piezas: Number(values.piezas),
          fabricante: values.fabricante || "",
          lote: values.lote || "",
          especificacion_id: values.especificacion_id,
          entregable_id: values.entregable_id,
          laboratorio_id: values.laboratorio_id,
          periodo_informe: Number(values.periodo_informe),
          frente_id: values.frente_id || null,
          subfrente_id: values.subfrente_id || null,
          comentarios: values.comentarios || null,
        });
      } else {
        const { marca, consecutivo, ...samplingValues } = values;
        const elemento = kind === "ndt" && values.etapa === "Taller"
          ? [String(marca || "").trim(), String(consecutivo || "").trim()].filter(Boolean).join(" - ")
          : null;
        await createSamplingRecord(kind, context, {
          ...samplingValues,
          ...(kind === "ndt" ? { elemento } : {}),
          fecha_muestreo: values.fecha_prueba,
          terracerias_ids: [],
        });
      }
      await onSaved();
      if (kind === "steel" || kind === "ndt" || kind === "masonry") resetSpecialForm();
      else {
        setValues({ fecha_prueba: localToday(), cantidad: 1, periodo_informe: 7, entidad: "Supervisión", probetas: 1, piezas: 1, rechazo: 0, tornillos_por_especificacion: [], tipo_conexion: "", metodo_pretension: "", dictamen: "", comentarios: "" });
        setAggregateCatalog((current) => ({ ...current, deliverables: [], specifications: [] }));
        setError("");
      }
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  const commonFields = kind !== "bolts" && kind !== "aggregates" && kind !== "masonry" && <>
    <label>Fecha del muestreo o prueba<input type="date" max={localToday()} value={String(values.fecha_prueba || "")} onChange={(event) => set("fecha_prueba", event.target.value)} required /></label>
    <label>Cantidad<input type="number" min="1" value={String(values.cantidad || "")} onChange={(event) => set("cantidad", event.target.value)} required /></label>
    <label>Frente<select value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))} required><option value="">Selecciona…</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
    <label>Subfrente<select value={String(values.subfrente_id || "")} onChange={(event) => set("subfrente_id", event.target.value)} disabled={!values.frente_id}><option value="">Sin subfrente</option>{subfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
    <label>Laboratorio<select value={String(values.laboratorio_id || "")} onChange={(event) => set("laboratorio_id", event.target.value)}><option value="">Sin laboratorio</option>{labs.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
    <label>Plazo del informe (días)<input type="number" min="0" value={String(values.periodo_informe || "")} onChange={(event) => set("periodo_informe", event.target.value)} required /></label>
  </>;

  const aggregateRequired = kind === "aggregates" && [
    values.fecha_prueba,
    values.actividad_material_id,
    values.contrato_actividad_material_id,
    values.lugar_muestreo,
    values.cantidad,
    values.periodo_informe,
    values.procedencia_id,
    values.laboratorio_id,
    values.frente_id,
    values.entregable_id,
    values.especificacion_id,
  ].some((value) => String(value ?? "").trim() === "");
  const masonryDateError = kind === "masonry" && values.fecha_fab && values.fecha_prueba && String(values.fecha_fab) > String(values.fecha_prueba)
    ? "La fecha de fabricación no puede ser posterior a la fecha de muestreo."
    : kind === "masonry" && values.fecha_fab && values.fecha_sum && String(values.fecha_fab) > String(values.fecha_sum)
      ? "La fecha de fabricación no puede ser posterior a la fecha de suministro."
      : "";
  const masonryRequired = kind === "masonry" && [values.fecha_prueba, values.geometria, values.material, values.tipo_pieza, values.ancho, values.alto, values.largo, values.resistencia, values.cantidad, values.piezas, values.especificacion_id, values.entregable_id, values.laboratorio_id, values.periodo_informe]
    .some((value) => String(value ?? "").trim() === "");

  if (kind === "steel") return <Modal title="Muestreo de acero para refuerzo" className="steel-sampling-modal" onClose={onClose}>
    <form className="special-sampling-form steel-sampling-form" onSubmit={submit}>
      {error && <div className="notice error full">{error}</div>}
      <label className="steel-date">Fecha de muestreo<input type="date" max={localToday()} value={String(values.fecha_prueba || "")} onChange={(event) => set("fecha_prueba", event.target.value)} required /></label>
      <label className="steel-designation">Designación<select value={String(values.designacion || "")} onChange={(event) => set("designacion", event.target.value)} required><option value="">Seleccione…</option>{STEEL_DESIGNATIONS.map((designation) => <option key={designation} value={designation}>{designation}</option>)}</select></label>
      <div className="steel-specification"><SelectField label="Especificación" value={values.especificacion_id} rows={specifications} onChange={(value) => void selectSteelSpecification(value)} required /></div>
      <label className="steel-grade">Grado<input value={String(values.grado || "")} onChange={(event) => set("grado", event.target.value)} required /></label>
      <label className="steel-amount">Muestras<input type="number" min="1" value={String(values.cantidad || "")} onChange={(event) => set("cantidad", event.target.value)} required /></label>
      <label className="steel-specimens">Probetas por muestra<input type="number" min="1" value={String(values.probetas || "")} onChange={(event) => set("probetas", event.target.value)} required /></label>
      <label className="steel-provider">Proveedor<input value={String(values.proveedor || "")} onChange={(event) => set("proveedor", event.target.value)} /></label>
      <label className="steel-heat">Colada<input value={String(values.colada || "")} onChange={(event) => set("colada", event.target.value)} /></label>
      <label className="steel-lot">Lote<input value={String(values.lote || "")} onChange={(event) => set("lote", event.target.value)} /></label>
      <div className="steel-lab-row full">
        <div className="steel-study"><SelectField label="Estudio" value={values.entregable_id} rows={steelStudies} onChange={(value) => set("entregable_id", value)} required disabled={!values.especificacion_id || steelStudiesLoading} /></div>
        <label className="steel-analysis-type">Tipo de análisis<select value={String(values.tipo_analisis || "")} onChange={(event) => set("tipo_analisis", event.target.value)} onInvalid={(event) => { event.preventDefault(); setError("Seleccione un Tipo de análisis."); }} required><option value="">Seleccione…</option>{steelAnalysisTypes.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
        <label className="steel-lab">Laboratorio<select value={String(values.laboratorio_id || "")} onChange={(event) => set("laboratorio_id", event.target.value)} required><option value="">Seleccione…</option>{labs.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label className="steel-deadline">Plazo del informe (días)<input type="number" min="0" value={String(values.periodo_informe || "")} onChange={(event) => set("periodo_informe", event.target.value)} required /></label>
      </div>
      <div className="steel-front-row full">
        <label className="steel-front">Frente<select value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))} required><option value="">Seleccione…</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label className="steel-subfront">Subfrente<select value={String(values.subfrente_id || "")} onChange={(event) => set("subfrente_id", event.target.value)} disabled={!values.frente_id}><option value="">Sin subfrente</option>{subfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
      </div>
      <label className="full">Comentarios<textarea rows={3} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} /></label>
      <div className="modal-actions full"><button type="button" className="button ghost" onClick={resetSpecialForm}>Resetear</button><button className="button primary" disabled={busy || steelStudiesLoading}>{busy ? "Guardando…" : "Guardar"}</button></div>
    </form>
  </Modal>;

  if (kind === "ndt") return <Modal title="Inspección de soldadura" className="ndt-sampling-modal" onClose={onClose}>
    <form className="special-sampling-form ndt-sampling-form" onSubmit={submit}>
      {error && <div className="notice error full">{error}</div>}
      <label className="ndt-date">Fecha<input type="date" max={localToday()} value={String(values.fecha_prueba || "")} onChange={(event) => set("fecha_prueba", event.target.value)} required /></label>
      <label className="ndt-place">Lugar<select value={String(values.etapa || "")} onChange={(event) => {
        const lugar = event.target.value;
        setValues((current) => ({
          ...current,
          etapa: lugar,
          ubicacion: lugar === "Taller" ? "" : current.ubicacion,
          marca: lugar === "Campo" ? "" : current.marca,
          consecutivo: lugar === "Campo" ? "" : current.consecutivo,
        }));
      }} required><option value="">Seleccione…</option><option value="Campo">Campo</option><option value="Taller">Taller</option></select></label>
      <div className="ndt-specification"><SelectField label="Especificación" value={values.especificacion_id} rows={ndtSpecifications} onChange={(value) => void selectNdtSpecification(value)} required /></div>
      <div className="ndt-deliverable"><SelectField label="Estudio" value={values.entregable_id} rows={ndtStudies} onChange={(value) => set("entregable_id", value)} required disabled={!values.especificacion_id || ndtCatalogLoading} /></div>
      <label className="ndt-front">Frente<select value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))} required><option value="">Seleccione…</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
      <label className="ndt-subfront">Subfrente<select value={String(values.subfrente_id || "")} onChange={(event) => set("subfrente_id", event.target.value)} disabled={!values.frente_id}><option value="">Sin subfrente</option>{subfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
      {values.etapa === "Taller" && <>
        <label className="ndt-mark">Marca<input value={String(values.marca || "")} onChange={(event) => set("marca", event.target.value)} required /></label>
        <label className="ndt-consecutive">Consecutivo<input value={String(values.consecutivo || "")} onChange={(event) => set("consecutivo", event.target.value)} required /></label>
      </>}
      <label className="ndt-lab">Laboratorio<select value={String(values.laboratorio_id || "")} onChange={(event) => set("laboratorio_id", event.target.value)} required><option value="">Seleccione…</option>{labs.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
      <label className="ndt-total">Total juntas<input type="number" min="1" step="1" value={String(values.cantidad || "")} onChange={(event) => set("cantidad", event.target.value)} required /></label>
      <label className="ndt-rejected">Rechazo<input type="number" min="0" step="1" max={Number(values.cantidad) || undefined} value={String(values.rechazo ?? 0)} onChange={(event) => set("rechazo", event.target.value)} required /></label>
      <label className="ndt-delivery">Plazo del informe (días)<input type="number" min="0" step="1" value={String(values.periodo_informe || "")} onChange={(event) => set("periodo_informe", event.target.value)} required /></label>
      {values.etapa === "Campo" && <label className="full">Ubicación de las juntas<textarea rows={3} value={String(values.ubicacion || "")} onChange={(event) => set("ubicacion", event.target.value)} required /></label>}
      <label className="full">Comentarios<textarea rows={3} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} /></label>
      <div className="modal-actions full"><button type="button" className="button ghost" onClick={resetSpecialForm}>Resetear</button><button className="button primary" disabled={busy || ndtCatalogLoading || !ndtStudies.length}>{busy ? "Guardando…" : "Guardar"}</button></div>
    </form>
  </Modal>;

  if (kind === "masonry") return <Modal title="Muestreo de piezas de mampostería" className="masonry-sampling-modal" onClose={onClose}>
    <form className="masonry-sampling-form" onSubmit={submit}>
      {error && <div className="notice error full">{error}</div>}
      {masonryDateError && error !== masonryDateError && <div className="notice error full">{masonryDateError}</div>}
      <section className="masonry-row masonry-primary-row full">
        <label>Muestreo<input type="date" max={localToday()} value={String(values.fecha_prueba || "")} onChange={(event) => set("fecha_prueba", event.target.value)} required /></label>
        <label>Fabricación<input type="date" max={String(values.fecha_prueba || localToday())} value={String(values.fecha_fab || "")} onChange={(event) => set("fecha_fab", event.target.value)} /></label>
        <label>Geometría<select value={String(values.geometria || "")} onChange={(event) => set("geometria", event.target.value)} required><option value="Bloque">Bloque</option><option value="Ladrillo">Ladrillo</option></select></label>
        <label>Material<select value={String(values.material || "")} onChange={(event) => set("material", event.target.value)} required><option value="Arcilla">Arcilla</option><option value="Concreto">Concreto</option></select></label>
        <label>Tipo de pieza<select value={String(values.tipo_pieza || "")} onChange={(event) => set("tipo_pieza", event.target.value)} required><option value="Maciza">Maciza</option><option value="Hueca">Hueca</option><option value="Multiperforada">Multiperforada</option><option value="De ajuste">De ajuste</option><option value="Accesoria">Accesoria</option></select></label>
      </section>
      <section className="masonry-row masonry-measures-row full">
        <label title="Dimensión nominal">Ancho, cm<input type="number" min="0" step="any" value={String(values.ancho || "")} onChange={(event) => set("ancho", event.target.value)} required /></label>
        <label title="Dimensión nominal">Alto, cm<input type="number" min="0" step="any" value={String(values.alto || "")} onChange={(event) => set("alto", event.target.value)} required /></label>
        <label title="Dimensión nominal">Largo, cm<input type="number" min="0" step="any" value={String(values.largo || "")} onChange={(event) => set("largo", event.target.value)} required /></label>
        <label>Res., kg/cm²<input type="number" min="0" step="any" value={String(values.resistencia || "")} onChange={(event) => set("resistencia", event.target.value)} required /></label>
        <label>Muestras<input type="number" min="1" step="1" value={String(values.cantidad || "")} onChange={(event) => set("cantidad", event.target.value)} required /></label>
        <label>Piezas<input type="number" min="1" step="1" value={String(values.piezas || "")} onChange={(event) => set("piezas", event.target.value)} required /></label>
      </section>
      <section className="masonry-row masonry-source-row full">
        <label>Suministro<input type="date" min={String(values.fecha_fab || "") || undefined} value={String(values.fecha_sum || "")} onChange={(event) => set("fecha_sum", event.target.value)} /></label>
        <label>Fabricante<input value={String(values.fabricante || "")} onChange={(event) => set("fabricante", event.target.value)} /></label>
        <label>Lote<input value={String(values.lote || "")} onChange={(event) => set("lote", event.target.value)} /></label>
      </section>
      <section className="masonry-row masonry-contract-row full">
        <div><SelectField label="Especificación" value={values.especificacion_id} rows={specifications} onChange={(value) => void selectMasonrySpecification(value)} required /></div>
        <div><SelectField label="Estudio" value={values.entregable_id} rows={masonryStudies} onChange={(value) => set("entregable_id", value)} required disabled={!values.especificacion_id || masonryStudiesLoading} /></div>
        <div><SelectField label="Laboratorio" value={values.laboratorio_id} rows={labs} onChange={(value) => set("laboratorio_id", value)} required /></div>
        <label>Plazo del informe (días)<input type="number" min="0" value={String(values.periodo_informe || "")} onChange={(event) => set("periodo_informe", event.target.value)} required /></label>
      </section>
      <section className="masonry-row masonry-location-row full">
        <label>Frente<select value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))}><option value="">Sin frente</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label>Subfrente<select value={String(values.subfrente_id || "")} onChange={(event) => set("subfrente_id", event.target.value)} disabled={!values.frente_id}><option value="">Sin subfrente</option>{subfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
      </section>
      <label className="full">Comentarios<textarea rows={3} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} /></label>
      <div className="modal-actions full"><button type="button" className="button ghost" onClick={resetSpecialForm}>Resetear</button><button className="button primary" disabled={busy || masonryStudiesLoading || Boolean(masonryDateError) || masonryRequired}>{busy ? "Guardando…" : "Guardar"}</button></div>
    </form>
  </Modal>;

  return <Modal title={kind === "bolts" ? "Inspección de conexiones atornilladas" : kind === "aggregates" ? "Muestreo de materiales pétreos y térreos" : `Nuevo registro · ${label}`} className={kind === "bolts" ? "bolt-inspection-modal" : kind === "aggregates" ? "aggregate-sampling-modal" : "modal-wide"} onClose={onClose}><form className={`form-grid sampling-form${kind === "bolts" ? " bolt-inspection-form" : ""}${kind === "aggregates" ? " aggregate-sampling-form" : ""}`} onSubmit={submit}>{error && <div className="notice error full">{error}</div>}{commonFields}
    {kind === "aggregates" && <>
      <div className="aggregate-main-row">
        <label className="aggregate-date">Fecha<input type="date" max={localToday()} value={String(values.fecha_prueba || "")} onChange={(event) => set("fecha_prueba", event.target.value)} required /></label>
        <div className="aggregate-material"><SelectField label="Material" value={values.contrato_actividad_material_id} rows={aggregateCatalog.activities} onChange={(value) => void selectAggregateMaterial(value)} required /></div>
        <div className="aggregate-specification"><SelectField label="Especificación" value={values.especificacion_id} rows={aggregateCatalog.specifications} onChange={(value) => void selectAggregateSpecification(value)} required /></div>
      </div>
      <div className="aggregate-origin"><SelectField label="Procedencia" value={values.procedencia_id} rows={aggregateCatalog.origins} onChange={(value) => set("procedencia_id", value)} required /></div>
      <label className="aggregate-lot">Lote<input value={String(values.lote || "")} onChange={(event) => set("lote", event.target.value)} /></label>
      <label className="aggregate-place">Muestreado en<select value={String(values.lugar_muestreo || "")} onChange={(event) => set("lugar_muestreo", event.target.value)} required><option value="">Seleccione…</option><option value="Banco">Banco</option><option value="Obra">Obra</option></select></label>
      <label className="aggregate-amount">Muestras<input type="number" min="1" step="1" value={String(values.cantidad || "")} onChange={(event) => set("cantidad", event.target.value)} required /></label>
      <div className="aggregate-front-row">
        <label className="aggregate-front">Frente<select value={String(values.frente_id || "")} onChange={(event) => setValues((current) => ({ ...current, frente_id: event.target.value, subfrente_id: "" }))} required><option value="">Seleccione…</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label className="aggregate-subfront">Subfrente<select value={String(values.subfrente_id || "")} onChange={(event) => set("subfrente_id", event.target.value)} disabled={!values.frente_id}><option value="">Sin subfrente</option>{subfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
      </div>
      <div className="aggregate-study-row">
        <div className="aggregate-deliverable"><SelectField label="Estudio" value={values.entregable_id} rows={aggregateCatalog.deliverables} onChange={(value) => set("entregable_id", value)} required disabled={!values.especificacion_id || aggregateLoading} /></div>
        <div className="aggregate-lab"><SelectField label="Laboratorio" value={values.laboratorio_id} rows={labs} onChange={(value) => set("laboratorio_id", value)} required /></div>
        <label className="aggregate-deadline" title="Plazo para entregar el informe, en días">Plazo del informe (días)<input type="number" min="0" value={String(values.periodo_informe || "")} onChange={(event) => set("periodo_informe", event.target.value)} required /></label>
      </div>
      <label className="full aggregate-comments">Comentarios<textarea rows={3} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} /></label>
      {aggregateLoading && <div className="full aggregate-loading">Actualizando opciones relacionadas…</div>}
    </>}
    {kind === "compaction" && <><SelectField label="Actividad / Material" value={values.actividad_material_id} rows={activities} onChange={(value) => set("actividad_material_id", value)} required /><label>Capa<input value={String(values.capa || "")} onChange={(event) => set("capa", event.target.value)} /></label><label>Tipo de obra<input value={String(values.tipo_obra || "")} onChange={(event) => set("tipo_obra", event.target.value)} /></label><label>Ubicación puntual<input value={String(values.ubicacion || "")} onChange={(event) => set("ubicacion", event.target.value)} /></label><label>Punto inicial<input value={String(values.punto_inicial || "")} onChange={(event) => set("punto_inicial", event.target.value)} /></label><label>Punto final<input value={String(values.punto_final || "")} onChange={(event) => set("punto_final", event.target.value)} /></label><label>Franja<input value={String(values.franja || "")} onChange={(event) => set("franja", event.target.value)} /></label><label>Cadena inicial<input type="number" step="any" value={String(values.cad_inicial || "")} onChange={(event) => set("cad_inicial", event.target.value)} /></label><label>Cadena final<input type="number" step="any" value={String(values.cad_final || "")} onChange={(event) => set("cad_final", event.target.value)} /></label></>}
    {kind === "coating" && <><SelectField label="Actividad / Material" value={values.actividad_material_id} rows={activities} onChange={(value) => set("actividad_material_id", value)} /><SelectField label="Estudio" value={values.entregable_id} rows={contractStudies} onChange={(value) => set("entregable_id", value)} required /></>}
    {kind === "bolts" && context && <BoltInspectionFields values={values} setValues={setValues} catalog={catalog} context={context} />}
    {kind !== "bolts" && kind !== "aggregates" && <label className="full">Comentarios<textarea rows={4} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} /></label>}
    <div className="modal-actions full"><button type="button" className="button ghost" onClick={onClose}>Cancelar</button><button className="button primary" disabled={busy || aggregateLoading || aggregateRequired}>{busy ? "Guardando…" : "Guardar"}</button></div>
  </form></Modal>;
}

function SelectField({ label, value, rows, onChange, required = false, disabled = false, fallback = [] }: { label: string; value: unknown; rows: JsonRecord[]; onChange: (value: string) => void; required?: boolean; disabled?: boolean; fallback?: Option[] }) {
  const items = rows.length ? options(rows) : fallback;
  return <label>{label}<select value={String(value || "")} onChange={(event) => onChange(event.target.value)} required={required} disabled={disabled}><option value="">{required ? "Selecciona…" : "No aplica"}</option>{items.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>;
}

function EditSamplingModal({ row, catalog, onClose, onSaved }: { row: JsonRecord; catalog: JsonRecord; onClose: () => void; onSaved: () => Promise<void> }) {
  const { context } = useSession();
  const source = (row.source || {}) as JsonRecord;
  const isBolt = row.record_type === "bolts";
  const [values, setValues] = useState<JsonRecord>(isBolt ? { ...source, tornillos_por_especificacion: Array.isArray(source.tornillos_por_especificacion) ? source.tornillos_por_especificacion : [] } : { periodo_informe: source.periodo_informe || "", turno: source.turno || "", comentarios: source.comentarios || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  function set(key: string, value: unknown) { setValues((current) => ({ ...current, [key]: value })); }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isBolt) {
        if (!context) return;
        await saveBoltReport(context, prepareBoltPayload(values), String(row.id));
      } else {
        if (!context) return;
        await updateSamplingRecord(context, String(row.id), values);
      }
      await onSaved();
    } catch (caught) { setError(getErrorMessage(caught)); } finally { setBusy(false); }
  }
  return <Modal title={isBolt ? "Inspección de conexiones atornilladas" : "Editar muestreo o prueba"} className={isBolt ? "bolt-inspection-modal" : ""} onClose={onClose}><form className={`form-grid${isBolt ? " bolt-inspection-form" : ""}`} onSubmit={submit}>{error && <div className="notice error full">{error}</div>}{isBolt && context ? <BoltInspectionFields values={values} setValues={setValues} catalog={catalog} context={context} /> : <><label>Plazo del informe (días)<input type="number" min="0" value={String(values.periodo_informe || "")} onChange={(event) => set("periodo_informe", event.target.value)} /></label><label>Turno<input value={String(values.turno || "")} onChange={(event) => set("turno", event.target.value)} /></label><label className="full">Comentarios<textarea rows={5} value={String(values.comentarios || "")} onChange={(event) => set("comentarios", event.target.value)} /></label></>}<div className="modal-actions full"><button type="button" className="button ghost" onClick={onClose}>Cancelar</button><button className="button primary" disabled={busy}>{busy ? "Guardando…" : "Guardar cambios"}</button></div></form></Modal>;
}
